import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AppShell,
  DataTable,
  PageContainer,
  PageHeader,
  SectionContainer,
  StatusBadge,
  UIButton,
  mapInfrastructureStatusToTone,
} from '../../features/ui-system';
import { commandSidebarItems } from '../../features/ui-foundation/commandMenu';
import { useOperationalDataset } from './hooks/useOperationalDataset';
import { clearStaffSession, isJwtExpired, resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import { getOperationalCoordinateWarning, isOperationallyValidCoordinatePair, parseCoordinatePair } from '../../utils/coordinateQuality';
import LeafletPinPicker from '../../components/LeafletPinPicker';
import { appendTicketUpdate, createTicket } from './utils/ticketingApi';

const API = `${resolveBackendBaseUrl()}/api`;

const typeLabel = {
  nas: 'Router/NAS',
  olt: 'OLT',
  odp: 'ODP',
};

const RELATION_TYPE_OPTIONS = [
  { value: 'primary', label: 'Primary' },
  { value: 'backup', label: 'Backup' },
  { value: 'balancing', label: 'Balancing' },
  { value: 'upstream', label: 'Upstream' },
  { value: 'downstream', label: 'Downstream' },
  { value: 'failover', label: 'Failover' },
];

const DEFAULT_RELATION_FORM = {
  from_router_id: '',
  to_router_id: '',
  relation_type: 'primary',
  priority_order: '',
  is_active: true,
  notes: '',
};

const normalizeRouterRoleLabel = (value) => {
  const token = String(value || '').trim().toLowerCase();
  if (token === 'load_balancer') return 'load_balancer';
  if (token === 'service_edge') return 'service_edge';
  return token || 'distribution';
};

const DEFAULT_OLT_FORM = {
  name: '',
  code: '',
  router_id: '',
  kriteria: 'live',
  location: '',
  latitude: '',
  longitude: '',
  x: '120',
  y: '140',
};

const DEFAULT_ODP_FORM = {
  name: '',
  code: '',
  router_id: '',
  kriteria: 'live',
  olt_id: '',
  address: '',
  latitude: '',
  longitude: '',
  x: '450',
  y: '180',
};

const DEFAULT_ROUTER_FORM = {
  site_id: '',
  name: '',
  identity: '',
  mgmt_ip: '',
  nas_identifier: '',
  router_type: 'pppoe',
  router_role: 'distribution',
  router_label: '',
  priority_order: '',
  notes: '',
  status: 'active',
  latitude: '',
  longitude: '',
};

const DEFAULT_SITE_FORM = {
  code: '',
  name: '',
  address: '',
  description: '',
  timezone: 'Asia/Makassar',
  status: 'active',
  latitude: '',
  longitude: '',
};

const hasOperationalCoordinates = (lat, lon) => isOperationallyValidCoordinatePair(lat, lon);

const parseCoordinateError = (lat, lon) => {
  const parsed = parseCoordinatePair(lat, lon);
  if (parsed.reason === 'missing') return '';
  if (parsed.reason === 'incomplete_pair') return 'Latitude dan Longitude harus diisi berpasangan.';
  if (parsed.reason === 'non_numeric') return 'Latitude/Longitude harus berupa angka.';
  if (parsed.reason === 'out_of_range') return 'Latitude harus -90..90 dan Longitude harus -180..180.';
  return '';
};

const healthLabel = (value) => String(value || 'unknown').toUpperCase();

const riskLabel = (prediction) => {
  const risk = String(prediction?.risk?.level || 'safe').toLowerCase();
  if (risk === 'critical') return 'CRITICAL';
  if (risk === 'high') return 'HIGH';
  if (risk === 'warning') return 'WARN';
  return 'SAFE';
};

const relationTelemetryTone = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'active') return 'ok';
  if (token === 'degraded') return 'warn';
  if (token === 'idle') return 'neutral';
  return 'neutral';
};

const ticketStatusTone = (status) => {
  const token = String(status || '').trim().toLowerCase();
  if (token === 'closed') return 'ok';
  if (token === 'mitigated') return 'info';
  if (token === 'investigating') return 'warn';
  return 'neutral';
};

const ticketPriorityTone = (priority) => {
  const token = String(priority || '').trim().toLowerCase();
  if (token === 'critical') return 'danger';
  if (token === 'high') return 'warn';
  if (token === 'medium') return 'info';
  return 'neutral';
};

export default function AdminInfrastructure() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, refresh, customers, odps, olts, nasRouters, incidents, tickets, capacityPredictions, lastUpdated } = useOperationalDataset(navigate);

  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedKey, setSelectedKey] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorType, setEditorType] = useState('olt');
  const [editorMode, setEditorMode] = useState('create');
  const [editingId, setEditingId] = useState('');
  const [oltForm, setOltForm] = useState({ ...DEFAULT_OLT_FORM });
  const [odpForm, setOdpForm] = useState({ ...DEFAULT_ODP_FORM });
  const [routerForm, setRouterForm] = useState({ ...DEFAULT_ROUTER_FORM });
  const [siteForm, setSiteForm] = useState({ ...DEFAULT_SITE_FORM });
  const [sites, setSites] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showOltMapPicker, setShowOltMapPicker] = useState(false);
  const [showOdpMapPicker, setShowOdpMapPicker] = useState(false);
  const [showRouterMapPicker, setShowRouterMapPicker] = useState(false);
  const [showSiteMapPicker, setShowSiteMapPicker] = useState(false);
  const [showOltLegacyFields, setShowOltLegacyFields] = useState(false);
  const [showOdpLegacyFields, setShowOdpLegacyFields] = useState(false);
  const [editorContextHint, setEditorContextHint] = useState('');
  const [editorContextMeta, setEditorContextMeta] = useState(null);
  const [routerCatalog, setRouterCatalog] = useState([]);
  const [relationsLoading, setRelationsLoading] = useState(false);
  const [relationsError, setRelationsError] = useState('');
  const [routerRelations, setRouterRelations] = useState([]);
  const [relationEditorOpen, setRelationEditorOpen] = useState(false);
  const [relationEditorMode, setRelationEditorMode] = useState('create');
  const [editingRelationId, setEditingRelationId] = useState('');
  const [relationSubmitting, setRelationSubmitting] = useState(false);
  const [showAllRelations, setShowAllRelations] = useState(false);
  const [relationForm, setRelationForm] = useState({ ...DEFAULT_RELATION_FORM });
  const handledActionRef = useRef('');
  const [ticketDraft, setTicketDraft] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assigned_to: '',
  });
  const [ticketUpdateNote, setTicketUpdateNote] = useState('');
  const [ticketSaving, setTicketSaving] = useState(false);
  const [showAllRelatedTickets, setShowAllRelatedTickets] = useState(false);
  const [showAllTicketLogs, setShowAllTicketLogs] = useState(false);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    if (!token || isJwtExpired(token)) {
      clearStaffSession();
      navigate('/dadu/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditingId('');
    setOltForm({ ...DEFAULT_OLT_FORM });
    setOdpForm({ ...DEFAULT_ODP_FORM });
    setRouterForm({ ...DEFAULT_ROUTER_FORM });
    setSiteForm({ ...DEFAULT_SITE_FORM });
    setShowOltMapPicker(false);
    setShowOdpMapPicker(false);
    setShowRouterMapPicker(false);
    setShowSiteMapPicker(false);
    setShowOltLegacyFields(false);
    setShowOdpLegacyFields(false);
    setEditorContextHint('');
    setEditorContextMeta(null);
  }, []);

  const openCreateOlt = useCallback((seed = {}) => {
    setEditorType('olt');
    setEditorMode('create');
    setEditingId('');
    setOltForm({
      ...DEFAULT_OLT_FORM,
      location: String(seed.location || seed.site || ''),
      kriteria: String(seed.kriteria || 'live').toLowerCase() === 'plan' ? 'plan' : 'live',
      latitude: seed.latitude !== undefined ? String(seed.latitude) : '',
      longitude: seed.longitude !== undefined ? String(seed.longitude) : '',
    });
    setShowOltMapPicker(false);
    setShowOltLegacyFields(false);
    setShowOdpLegacyFields(false);
    setEditorContextHint(String(seed.contextHint || ''));
    setEditorContextMeta(seed.contextMeta || null);
    setEditorOpen(true);
  }, []);

  const openCreateOdp = useCallback((seed = {}) => {
    setEditorType('odp');
    setEditorMode('create');
    setEditingId('');
    setOdpForm({
      ...DEFAULT_ODP_FORM,
      olt_id: String(seed.olt_id || seed.oltId || ''),
      address: String(seed.address || seed.site || ''),
      kriteria: String(seed.kriteria || 'live').toLowerCase() === 'plan' ? 'plan' : 'live',
      latitude: seed.latitude !== undefined ? String(seed.latitude) : '',
      longitude: seed.longitude !== undefined ? String(seed.longitude) : '',
    });
    setShowOdpMapPicker(false);
    setShowOltLegacyFields(false);
    setShowOdpLegacyFields(false);
    setEditorContextHint(String(seed.contextHint || ''));
    setEditorContextMeta(seed.contextMeta || null);
    setEditorOpen(true);
  }, []);

  const openEditOlt = useCallback((olt) => {
    if (!olt) return;
    setEditorType('olt');
    setEditorMode('edit');
    setEditingId(String(olt.id || ''));
    setOltForm({
      name: String(olt.name || ''),
      code: String(olt.code || ''),
      router_id: String(olt.router_id || ''),
      kriteria: String(olt.kriteria || 'live').toLowerCase() === 'plan' ? 'plan' : 'live',
      location: String(olt.location || olt.site || ''),
      latitude: olt.latitude === null || olt.latitude === undefined ? '' : String(olt.latitude),
      longitude: olt.longitude === null || olt.longitude === undefined ? '' : String(olt.longitude),
      x: olt.x === null || olt.x === undefined ? DEFAULT_OLT_FORM.x : String(olt.x),
      y: olt.y === null || olt.y === undefined ? DEFAULT_OLT_FORM.y : String(olt.y),
    });
    setShowOltMapPicker(true);
    setShowOltLegacyFields(false);
    setShowOdpLegacyFields(false);
    setEditorContextHint('');
    setEditorContextMeta(null);
    setEditorOpen(true);
  }, []);

  const openEditOdp = useCallback((odp) => {
    if (!odp) return;
    const linkedOlt = olts.find((item) => String(item.id) === String(odp.olt_id || ''));
    setEditorType('odp');
    setEditorMode('edit');
    setEditingId(String(odp.id || ''));
    setOdpForm({
      name: String(odp.name || ''),
      code: String(odp.code || ''),
      router_id: String(odp.router_id || linkedOlt?.router_id || ''),
      kriteria: String(odp.kriteria || 'live').toLowerCase() === 'plan' ? 'plan' : 'live',
      olt_id: String(odp.olt_id || ''),
      address: String(odp.address || odp.site || ''),
      latitude: odp.latitude === null || odp.latitude === undefined ? '' : String(odp.latitude),
      longitude: odp.longitude === null || odp.longitude === undefined ? '' : String(odp.longitude),
      x: odp.x === null || odp.x === undefined ? DEFAULT_ODP_FORM.x : String(odp.x),
      y: odp.y === null || odp.y === undefined ? DEFAULT_ODP_FORM.y : String(odp.y),
    });
    setShowOdpMapPicker(true);
    setShowOltLegacyFields(false);
    setShowOdpLegacyFields(false);
    setEditorContextHint('');
    setEditorContextMeta(null);
    setEditorOpen(true);
  }, [olts]);

  const openCreateRouter = useCallback((seed = {}) => {
    setEditorType('router');
    setEditorMode('create');
    setEditingId('');
    setRouterForm({
      ...DEFAULT_ROUTER_FORM,
      site_id: String(seed.site_id || seed.siteId || ''),
      name: String(seed.name || ''),
      latitude: seed.latitude !== undefined ? String(seed.latitude) : '',
      longitude: seed.longitude !== undefined ? String(seed.longitude) : '',
    });
    setEditorContextHint(String(seed.contextHint || ''));
    setEditorContextMeta(seed.contextMeta || null);
    setShowRouterMapPicker(false);
    setShowSiteMapPicker(false);
    setEditorOpen(true);
  }, []);

  const openEditRouter = useCallback((router) => {
    if (!router) return;
    setEditorType('router');
    setEditorMode('edit');
    setEditingId(String(router.id || ''));
    setRouterForm({
      site_id: String(router.site_id || ''),
      name: String(router.name || ''),
      identity: String(router.identity || ''),
      mgmt_ip: String(router.mgmt_ip || ''),
      nas_identifier: String(router.nas_identifier || ''),
      router_type: String(router.router_type || 'pppoe'),
      router_role: String(router.router_role || 'distribution'),
      router_label: String(router.router_label || ''),
      priority_order: router.priority_order === null || router.priority_order === undefined ? '' : String(router.priority_order),
      notes: String(router.notes || ''),
      status: String(router.status || 'active'),
      latitude: router.latitude === null || router.latitude === undefined ? '' : String(router.latitude),
      longitude: router.longitude === null || router.longitude === undefined ? '' : String(router.longitude),
    });
    setEditorContextHint('');
    setEditorContextMeta(null);
    setShowRouterMapPicker(true);
    setShowSiteMapPicker(false);
    setEditorOpen(true);
  }, []);

  const openCreateSite = useCallback((seed = {}) => {
    setEditorType('site');
    setEditorMode('create');
    setEditingId('');
    setSiteForm({
      ...DEFAULT_SITE_FORM,
      code: String(seed.code || ''),
      name: String(seed.name || ''),
      latitude: seed.latitude !== undefined ? String(seed.latitude) : '',
      longitude: seed.longitude !== undefined ? String(seed.longitude) : '',
    });
    setEditorContextHint(String(seed.contextHint || ''));
    setEditorContextMeta(seed.contextMeta || null);
    setShowRouterMapPicker(false);
    setShowSiteMapPicker(false);
    setEditorOpen(true);
  }, []);

  const openEditSite = useCallback((site) => {
    if (!site) return;
    setEditorType('site');
    setEditorMode('edit');
    setEditingId(String(site.id || ''));
    setSiteForm({
      code: String(site.code || ''),
      name: String(site.name || ''),
      address: String(site.address || ''),
      description: String(site.description || ''),
      timezone: String(site.timezone || 'Asia/Makassar'),
      status: String(site.status || 'active'),
      latitude: site.latitude === null || site.latitude === undefined ? '' : String(site.latitude),
      longitude: site.longitude === null || site.longitude === undefined ? '' : String(site.longitude),
    });
    setEditorContextHint('');
    setEditorContextMeta(null);
    setShowRouterMapPicker(false);
    setShowSiteMapPicker(true);
    setEditorOpen(true);
  }, []);

  const handleMapLocationChange = useCallback((target, latitude, longitude) => {
    const next = {
      latitude: Number(latitude).toFixed(6),
      longitude: Number(longitude).toFixed(6),
    };
    if (target === 'olt') {
      setOltForm((prev) => ({ ...prev, ...next }));
      return;
    }
    if (target === 'odp') {
      setOdpForm((prev) => ({ ...prev, ...next }));
      return;
    }
    if (target === 'router') {
      setRouterForm((prev) => ({ ...prev, ...next }));
      return;
    }
    if (target === 'site') {
      setSiteForm((prev) => ({ ...prev, ...next }));
    }
  }, []);

  const handleGetCurrentLocation = useCallback((target) => {
    if (!navigator?.geolocation) {
      toast.error('Browser tidak mendukung akses lokasi.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        handleMapLocationChange(target, position.coords.latitude, position.coords.longitude);
        if (target === 'olt') {
          setShowOltMapPicker(true);
        } else if (target === 'odp') {
          setShowOdpMapPicker(true);
        } else if (target === 'router') {
          setShowRouterMapPicker(true);
        } else if (target === 'site') {
          setShowSiteMapPicker(true);
        }
        toast.success('Lokasi terkini berhasil diambil.');
      },
      (geoError) => {
        const message = geoError?.code === 1
          ? 'Izin lokasi ditolak.'
          : geoError?.code === 2
            ? 'Lokasi tidak tersedia.'
            : 'Gagal mengambil lokasi saat ini.';
        toast.error(message);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }, [handleMapLocationChange]);

  const resolveSiteCoordinates = useCallback((siteValue, target = 'all') => {
    const needle = String(siteValue || '').trim().toLowerCase();
    if (!needle) return null;

    const siteMatch = (value) => String(value || '').trim().toLowerCase() === needle;
    const pickCoordinates = (item) => {
      if (item && hasOperationalCoordinates(item.latitude, item.longitude)) {
        return { latitude: Number(item.latitude), longitude: Number(item.longitude) };
      }
      return null;
    };

    const matchedSite = sites.find((item) => (
      siteMatch(item.id) || siteMatch(item.code) || siteMatch(item.name)
    ));
    const siteCoords = pickCoordinates(matchedSite);
    if (siteCoords) return siteCoords;

    if (target === 'olt') {
      const matchedOlt = olts.find((item) => siteMatch(item.location || item.site || item.site_id));
      const coords = pickCoordinates(matchedOlt);
      if (coords) return coords;
    }
    if (target === 'odp') {
      const matchedOdp = odps.find((item) => siteMatch(item.address || item.site || item.site_id));
      const coords = pickCoordinates(matchedOdp);
      if (coords) return coords;
    }

    const fallbackOlt = olts.find((item) => siteMatch(item.location || item.site || item.site_id));
    const fallbackOltCoords = pickCoordinates(fallbackOlt);
    if (fallbackOltCoords) return fallbackOltCoords;

    const fallbackOdp = odps.find((item) => siteMatch(item.address || item.site || item.site_id));
    const fallbackOdpCoords = pickCoordinates(fallbackOdp);
    if (fallbackOdpCoords) return fallbackOdpCoords;

    return null;
  }, [sites, olts, odps]);

  const handleSiteInputChange = useCallback((target, value) => {
    if (target === 'olt') {
      setOltForm((prev) => ({ ...prev, location: value }));
    } else {
      setOdpForm((prev) => ({ ...prev, address: value }));
    }

    const matchedCoordinates = resolveSiteCoordinates(value, target);
    if (matchedCoordinates) {
      handleMapLocationChange(target, matchedCoordinates.latitude, matchedCoordinates.longitude);
      if (target === 'olt') setShowOltMapPicker(true);
      if (target === 'odp') setShowOdpMapPicker(true);
    }
  }, [handleMapLocationChange, resolveSiteCoordinates]);

  const rows = useMemo(() => {
    const nasRows = nasRouters.map((item) => ({
      key: `nas:${item.id || item.router_id}`,
      type: 'nas',
      id: String(item.id || item.router_id || '-'),
      apiRouterId: String(item.router_id || ''),
      nasIdentifier: String(item.nas_identifier || ''),
      name: item.name || item.nas_identifier || '-',
      site_id: String(item.site_id || item.siteId || '').trim(),
      site_name: String(item.site_name || item.site || item.site_id || '-').trim() || '-',
      site: String(item.site_name || item.site || item.site_id || '-').trim() || '-',
      status: item.status || item.connection_status || 'unknown',
      capacity: `CPU ${Math.round(Number(item.cpu_usage || 0))}% / MEM ${Math.round(Number(item.memory_usage || 0))}%`,
      latitude: item.latitude ?? null,
      longitude: item.longitude ?? null,
    }));

    const oltRows = olts.map((item) => ({
      key: `olt:${item.id}`,
      type: 'olt',
      id: String(item.id || '-'),
      name: item.name || item.code || '-',
      site_id: String(item.site_id || '').trim(),
      site_name: String(item.site_name || item.location || item.site || item.site_id || '-').trim() || '-',
      site: String(item.site_name || item.location || item.site || item.site_id || '-').trim() || '-',
      status: item.status || item.health_status || 'online',
      capacity: `PON ${Math.round(Number(item.pon_usage || 0))}%`,
    }));

    const odpRows = odps.map((item) => {
      const prediction = capacityPredictions.byOdpId[String(item.id)];
      return {
        key: `odp:${item.id}`,
        type: 'odp',
        id: String(item.id || '-'),
        name: item.name || item.code || '-',
        site_id: String(item.site_id || '').trim(),
        site_name: String(item.site_name || item.site || item.address || item.site_id || '-').trim() || '-',
        site: String(item.site_name || item.site || item.address || item.site_id || '-').trim() || '-',
        status: prediction?.risk?.level || item.status || 'safe',
        capacity: prediction ? `${prediction.used}/${prediction.capacity} (${prediction.occupancyPercent}%)` : 'N/A',
      };
    });

    return [...nasRows, ...oltRows, ...odpRows];
  }, [nasRouters, olts, odps, capacityPredictions.byOdpId]);

  const filteredRows = useMemo(() => {
    const keyword = String(query || '').trim().toLowerCase();
    return rows.filter((row) => {
      if (typeFilter !== 'all' && row.type !== typeFilter) return false;
      if (!keyword) return true;
      const hay = `${row.id} ${row.name} ${row.site} ${row.type}`.toLowerCase();
      return hay.includes(keyword);
    });
  }, [rows, query, typeFilter]);

  const oltCoordinateError = useMemo(
    () => parseCoordinateError(oltForm.latitude, oltForm.longitude),
    [oltForm.latitude, oltForm.longitude],
  );
  const oltCoordinateWarning = useMemo(
    () => getOperationalCoordinateWarning(oltForm.latitude, oltForm.longitude),
    [oltForm.latitude, oltForm.longitude],
  );

  const odpCoordinateError = useMemo(
    () => parseCoordinateError(odpForm.latitude, odpForm.longitude),
    [odpForm.latitude, odpForm.longitude],
  );
  const odpCoordinateWarning = useMemo(
    () => getOperationalCoordinateWarning(odpForm.latitude, odpForm.longitude),
    [odpForm.latitude, odpForm.longitude],
  );

  const routerCoordinateError = useMemo(
    () => parseCoordinateError(routerForm.latitude, routerForm.longitude),
    [routerForm.latitude, routerForm.longitude],
  );
  const routerCoordinateWarning = useMemo(
    () => getOperationalCoordinateWarning(routerForm.latitude, routerForm.longitude),
    [routerForm.latitude, routerForm.longitude],
  );

  const siteCoordinateError = useMemo(
    () => parseCoordinateError(siteForm.latitude, siteForm.longitude),
    [siteForm.latitude, siteForm.longitude],
  );
  const siteCoordinateWarning = useMemo(
    () => getOperationalCoordinateWarning(siteForm.latitude, siteForm.longitude),
    [siteForm.latitude, siteForm.longitude],
  );

  const siteSuggestions = useMemo(() => {
    const values = new Set();
    sites.forEach((item) => {
      const value = String(item?.id || item?.code || item?.name || '').trim();
      if (value) values.add(value);
      const altName = String(item?.name || '').trim();
      if (altName) values.add(altName);
    });
    olts.forEach((item) => {
      const value = String(item?.location || item?.site || item?.site_id || '').trim();
      if (value) values.add(value);
    });
    odps.forEach((item) => {
      const value = String(item?.address || item?.site || item?.site_id || '').trim();
      if (value) values.add(value);
    });
    nasRouters.forEach((item) => {
      const value = String(item?.site_name || item?.site || item?.site_id || '').trim();
      if (value) values.add(value);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [sites, olts, odps, nasRouters]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const nodeType = String(params.get('nodeType') || '').trim().toLowerCase();
    const nodeId = String(params.get('nodeId') || '').trim();
    const sourceType = String(params.get('sourceType') || '').trim().toLowerCase();
    const sourceId = String(params.get('sourceId') || '').trim();
    if (nodeType && nodeId) {
      setSelectedKey(`${nodeType}:${nodeId}`);
      return;
    }
    if (sourceType && sourceId) {
      setSelectedKey(`${sourceType}:${sourceId}`);
      return;
    }
    const incidentId = String(params.get('incidentId') || '').trim();
    if (incidentId) {
      const linkedIncident = incidents.find((item) => String(item.id) === incidentId);
      const infraRef = linkedIncident?.impactedEntities?.find((item) => (
        String(item).startsWith('nas:') || String(item).startsWith('olt:') || String(item).startsWith('odp:')
      ));
      if (infraRef) setSelectedKey(String(infraRef));
    }
  }, [location.search, incidents]);

  React.useEffect(() => {
    if (!location.search) return;
    if (handledActionRef.current === location.search) return;

    const params = new URLSearchParams(location.search || '');
    const action = String(params.get('action') || '').trim().toLowerCase();
    if (!action) return;

    const sourceType = String(params.get('sourceType') || '').trim().toLowerCase();
    const sourceId = String(params.get('sourceId') || '').trim();
    const nodeType = String(params.get('nodeType') || params.get('sourceType') || '').trim().toLowerCase();
    const nodeId = String(params.get('nodeId') || params.get('sourceId') || '').trim();

    const resolveContextCoordinates = () => {
      const latParam = params.get('latitude');
      const lonParam = params.get('longitude');
      if (hasOperationalCoordinates(latParam, lonParam)) {
        return { coordinates: { latitude: Number(latParam), longitude: Number(lonParam) }, siteNotFound: false };
      }

      const byNodeType = String(nodeType || sourceType || '').trim().toLowerCase();
      const byNodeId = String(nodeId || sourceId || '').trim();
      if (byNodeType && byNodeId) {
        if (byNodeType === 'olt') {
          const olt = olts.find((item) => String(item.id) === byNodeId);
          if (olt && hasOperationalCoordinates(olt.latitude, olt.longitude)) return { coordinates: { latitude: Number(olt.latitude), longitude: Number(olt.longitude) }, siteNotFound: false };
        }
        if (byNodeType === 'odp') {
          const odp = odps.find((item) => String(item.id) === byNodeId);
          if (odp && hasOperationalCoordinates(odp.latitude, odp.longitude)) return { coordinates: { latitude: Number(odp.latitude), longitude: Number(odp.longitude) }, siteNotFound: false };
        }
      }

      const siteHint = String(params.get('siteId') || params.get('site') || '').trim().toLowerCase();
      if (siteHint) {
        const matchedOlt = olts.find((item) => String(item.site_id || item.location || item.site || '').trim().toLowerCase() === siteHint && hasOperationalCoordinates(item.latitude, item.longitude));
        if (matchedOlt) return { coordinates: { latitude: Number(matchedOlt.latitude), longitude: Number(matchedOlt.longitude) }, siteNotFound: false };
        const matchedOdp = odps.find((item) => String(item.site_id || item.address || item.site || '').trim().toLowerCase() === siteHint && hasOperationalCoordinates(item.latitude, item.longitude));
        if (matchedOdp) return { coordinates: { latitude: Number(matchedOdp.latitude), longitude: Number(matchedOdp.longitude) }, siteNotFound: false };
        return { coordinates: null, siteNotFound: true };
      }
      return { coordinates: null, siteNotFound: false };
    };

    if (action === 'create-olt') {
      const { coordinates: contextCoordinates, siteNotFound } = resolveContextCoordinates();
      const contextMeta = {
        site: String(params.get('siteId') || params.get('site') || '').trim(),
        sourceType: String(params.get('sourceType') || params.get('nodeType') || '').trim(),
        sourceId: String(params.get('sourceId') || params.get('nodeId') || '').trim(),
        hasCoordinates: Boolean(contextCoordinates),
      };
      const hasTopologyContext = Boolean(
        params.get('siteId')
        || params.get('site')
        || params.get('sourceType')
        || params.get('sourceId')
        || params.get('nodeType')
        || params.get('nodeId'),
      );
      handledActionRef.current = location.search;
      openCreateOlt({
        site: params.get('site'),
        location: params.get('location'),
        latitude: contextCoordinates?.latitude ?? params.get('latitude'),
        longitude: contextCoordinates?.longitude ?? params.get('longitude'),
        contextHint: hasTopologyContext
          ? contextCoordinates
            ? 'Lokasi diprefill dari konteks topology/site terpilih.'
            : siteNotFound
              ? 'Site konteks tidak ditemukan pada data aktif. Pilih titik lokasi secara manual.'
              : 'Konteks topology terdeteksi, tetapi koordinat belum tersedia. Gunakan peta untuk menentukan titik.'
          : '',
        contextMeta,
      });
      return;
    }

    if (action === 'create-odp') {
      const { coordinates: contextCoordinates, siteNotFound } = resolveContextCoordinates();
      const contextMeta = {
        site: String(params.get('siteId') || params.get('site') || '').trim(),
        sourceType: String(params.get('sourceType') || params.get('nodeType') || '').trim(),
        sourceId: String(params.get('sourceId') || params.get('nodeId') || params.get('oltId') || '').trim(),
        hasCoordinates: Boolean(contextCoordinates),
      };
      const hasTopologyContext = Boolean(
        params.get('siteId')
        || params.get('site')
        || params.get('sourceType')
        || params.get('sourceId')
        || params.get('nodeType')
        || params.get('nodeId')
        || params.get('oltId')
        || params.get('olt_id'),
      );
      handledActionRef.current = location.search;
      openCreateOdp({
        olt_id: params.get('oltId') || params.get('olt_id'),
        site: params.get('site'),
        address: params.get('address'),
        latitude: contextCoordinates?.latitude ?? params.get('latitude'),
        longitude: contextCoordinates?.longitude ?? params.get('longitude'),
        contextHint: hasTopologyContext
          ? contextCoordinates
            ? 'Titik awal diambil dari site/node topology yang dipilih.'
            : siteNotFound
              ? 'Site konteks tidak ditemukan pada data aktif. Pilih titik lokasi secara manual.'
              : 'Konteks topology aktif, tetapi koordinat awal tidak ditemukan. Pilih titik lewat peta.'
          : '',
        contextMeta,
      });
      return;
    }

    if ((action === 'edit-olt' || action === 'edit') && nodeType === 'olt' && nodeId) {
      const target = olts.find((item) => String(item.id) === nodeId);
      if (!target) return;
      handledActionRef.current = location.search;
      openEditOlt(target);
      return;
    }

    if ((action === 'edit-odp' || action === 'edit') && nodeType === 'odp' && nodeId) {
      const target = odps.find((item) => String(item.id) === nodeId);
      if (!target) return;
      handledActionRef.current = location.search;
      openEditOdp(target);
    }
  }, [location.search, olts, odps, openCreateOlt, openCreateOdp, openEditOlt, openEditOdp]);

  const selectedRow = useMemo(
    () => filteredRows.find((item) => item.key === selectedKey) || filteredRows[0] || null,
    [filteredRows, selectedKey],
  );

  const relatedCustomers = useMemo(() => {
    if (!selectedRow) return [];
    if (selectedRow.type === 'nas') {
      return customers.filter((item) => String(item.router_id || '') === selectedRow.id);
    }
    if (selectedRow.type === 'olt') {
      const odpIds = new Set(odps.filter((item) => String(item.olt_id || '') === selectedRow.id).map((item) => String(item.id)));
      return customers.filter((item) => odpIds.has(String(item.linked_odp_id || item.odp_id || '')));
    }
    return customers.filter((item) => String(item.linked_odp_id || item.odp_id || '') === selectedRow.id);
  }, [selectedRow, customers, odps]);

  const relatedIncidents = useMemo(() => {
    if (!selectedRow) return [];
    const key = `${selectedRow.type}:${selectedRow.id}`;
    return incidents.filter((incident) => incident.impactedEntities.includes(key));
  }, [selectedRow, incidents]);

  const relatedTickets = useMemo(() => {
    if (!selectedRow) return [];
    const sourceType = selectedRow.type === 'nas' ? 'router' : selectedRow.type === 'odp' || selectedRow.type === 'olt' ? 'site' : selectedRow.type;
    return tickets.filter((ticket) => {
      const ticketSourceType = String(ticket.sourceType || ticket.source_type || '').toLowerCase();
      const ticketSourceId = String(ticket.sourceId || ticket.source_id || '');
      const ticketRouterId = String(ticket.routerId || ticket.router_id || ticket.nodeId || ticket.node_id || '');
      const ticketSiteId = String(ticket.siteId || ticket.site_id || '');
      if (selectedRow.type === 'nas' && ticketRouterId === selectedRow.id) return true;
      if ((selectedRow.type === 'olt' || selectedRow.type === 'odp') && ticketSiteId && selectedRow.site_id && ticketSiteId === String(selectedRow.site_id)) return true;
      if (ticketSourceType === sourceType && ticketSourceId === selectedRow.id) return true;
      if (relatedIncidents.some((incident) => String(incident.id) === String(ticket.incidentId || ticket.incident_id || ''))) return true;
      return false;
    });
  }, [selectedRow, tickets, relatedIncidents]);

  const activeRelatedTickets = useMemo(
    () => relatedTickets.filter((ticket) => String(ticket.status || '').toLowerCase() !== 'closed'),
    [relatedTickets],
  );

  const latestRelatedTicket = useMemo(
    () => relatedTickets[0] || null,
    [relatedTickets],
  );

  const selectedPrediction = useMemo(() => {
    if (!selectedRow || selectedRow.type !== 'odp') return null;
    return capacityPredictions.byOdpId[selectedRow.id] || null;
  }, [selectedRow, capacityPredictions.byOdpId]);

  const selectedRouterContext = useMemo(() => {
    if (!selectedRow || selectedRow.type !== 'nas') {
      return {
        isValid: false,
        resolvedRouterId: '',
        reason: 'Relasi router hanya tersedia untuk node Router/NAS.',
      };
    }

    const selectedNas = nasRouters.find((item) => (
      String(item.id || item.router_id || '').trim() === String(selectedRow.id || '').trim()
      || String(item.router_id || '').trim() === String(selectedRow.apiRouterId || '').trim()
    ));

    const candidates = new Set(
      [
        selectedRow.id,
        selectedRow.apiRouterId,
        selectedRow.nasIdentifier,
        selectedNas?.id,
        selectedNas?.router_id,
        selectedNas?.nas_identifier,
      ]
        .map((value) => String(value || '').trim())
        .filter(Boolean),
    );

    const matchedRouter = routerCatalog.find((router) => {
      const routerId = String(router.id || '').trim();
      const nasIdentifier = String(router.nas_identifier || '').trim();
      return candidates.has(routerId) || (nasIdentifier && candidates.has(nasIdentifier));
    });

    if (!matchedRouter) {
      return {
        isValid: false,
        resolvedRouterId: '',
        reason: 'Relasi router belum tersedia untuk node ini. Sinkronisasi identifier router diperlukan.',
      };
    }

    return {
      isValid: true,
      resolvedRouterId: String(matchedRouter.id || '').trim(),
      reason: '',
    };
  }, [selectedRow, nasRouters, routerCatalog]);

  const selectedRouterId = selectedRouterContext.resolvedRouterId;
  const selectedNasRecord = useMemo(() => (
    selectedRow?.type === 'nas'
      ? nasRouters.find((item) => (
        String(item.id || item.router_id || '').trim() === String(selectedRow.id || '').trim()
        || String(item.router_id || '').trim() === String(selectedRow.apiRouterId || '').trim()
      )) || null
      : null
  ), [selectedRow, nasRouters]);
  const selectedRouterRecord = useMemo(() => (
    selectedRouterId
      ? routerCatalog.find((item) => String(item.id || '').trim() === String(selectedRouterId))
      : null
  ), [selectedRouterId, routerCatalog]);
  const selectedSiteRecord = useMemo(() => {
    if (!selectedRow) return null;
    if (selectedRouterRecord?.site_id) {
      const byRouterSite = sites.find((site) => String(site.id || '').trim() === String(selectedRouterRecord.site_id || '').trim());
      if (byRouterSite) return byRouterSite;
    }
    const siteToken = String(selectedRow.site_id || selectedRow.site || '').trim().toLowerCase();
    if (!siteToken || siteToken === '-') return null;
    return sites.find((site) => (
      String(site.id || '').trim().toLowerCase() === siteToken
      || String(site.code || '').trim().toLowerCase() === siteToken
      || String(site.name || '').trim().toLowerCase() === siteToken
    )) || null;
  }, [selectedRow, selectedRouterRecord, sites]);

  const routerOptions = useMemo(() => {
    const fromCatalog = routerCatalog.map((router) => ({
      id: String(router.id || '').trim(),
      name: String(router.name || router.identity || router.nas_identifier || router.id || '-'),
      router_role: normalizeRouterRoleLabel(router.router_role || router.role),
      site: String(router.site_name || router.site || router.site_id || '-'),
      nas_identifier: String(router.nas_identifier || '').trim(),
      identity: String(router.identity || '').trim(),
    })).filter((router) => router.id);

    if (fromCatalog.length > 0) return fromCatalog;
    return nasRouters.map((router) => ({
      id: String(router.id || router.router_id || '').trim(),
      name: String(router.name || router.identity || router.nas_identifier || router.id || '-'),
      router_role: normalizeRouterRoleLabel(router.router_role || router.role),
      site: String(router.site_name || router.site || router.site_id || '-'),
      nas_identifier: String(router.nas_identifier || '').trim(),
      identity: String(router.identity || '').trim(),
    })).filter((router) => router.id);
  }, [routerCatalog, nasRouters]);

  const routerOptionById = useMemo(() => {
    const map = new Map();
    routerOptions.forEach((item) => map.set(item.id, item));
    return map;
  }, [routerOptions]);

  const relationFormError = useMemo(() => {
    const fromRouterId = String(relationForm.from_router_id || '').trim();
    const toRouterId = String(relationForm.to_router_id || '').trim();
    const relationType = String(relationForm.relation_type || '').trim().toLowerCase();
    const priorityRaw = String(relationForm.priority_order ?? '').trim();

    if (!fromRouterId) return 'Router sumber wajib dipilih.';
    if (!toRouterId) return 'Router tujuan wajib dipilih.';
    if (fromRouterId === toRouterId) return 'Router sumber dan tujuan tidak boleh sama.';
    if (!routerOptionById.has(fromRouterId) || !routerOptionById.has(toRouterId)) {
      return 'Router sumber/tujuan tidak valid atau sudah tidak tersedia.';
    }
    if (!RELATION_TYPE_OPTIONS.some((item) => item.value === relationType)) {
      return 'Relation type tidak valid.';
    }
    if (priorityRaw) {
      const priority = Number(priorityRaw);
      if (!Number.isInteger(priority) || priority < 0) return 'Priority order harus angka bulat >= 0.';
    }

    const duplicate = routerRelations.find((item) => (
      String(item.from_router_id || '').trim() === fromRouterId
      && String(item.to_router_id || '').trim() === toRouterId
      && String(item.relation_type || '').trim().toLowerCase() === relationType
      && String(item.id || '') !== String(editingRelationId || '')
    ));
    if (duplicate) return 'Kombinasi from + to + relation type sudah ada.';

    return '';
  }, [relationForm, routerRelations, editingRelationId, routerOptionById]);

  const relationStats = useMemo(() => {
    const total = routerRelations.length;
    const active = routerRelations.filter((item) => Boolean(item.is_active)).length;
    return { total, active, inactive: Math.max(0, total - active) };
  }, [routerRelations]);

  const relationItemsToRender = useMemo(() => {
    if (showAllRelations) return routerRelations;
    return routerRelations.slice(0, 3);
  }, [routerRelations, showAllRelations]);

  const relationsViewState = useMemo(() => {
    if (!selectedRow || selectedRow.type !== 'nas') return 'unavailable';
    if (!selectedRouterContext.isValid) return 'unavailable';
    if (relationsLoading) return 'loading';
    if (relationsError) return 'error';
    if (!routerRelations.length) return 'empty';
    return 'success';
  }, [selectedRow, selectedRouterContext.isValid, relationsLoading, relationsError, routerRelations.length]);

  const topologyRouterContext = useMemo(() => {
    if (!selectedRow || selectedRow.type !== 'nas') return { routerId: '', latitude: null, longitude: null };

    const candidates = new Set(
      [
        selectedRow.id,
        selectedRow.apiRouterId,
        selectedRow.nasIdentifier,
        selectedNasRecord?.id,
        selectedNasRecord?.router_id,
        selectedNasRecord?.nas_identifier,
      ]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean),
    );

    const mappedRouter = routerCatalog.find((router) => {
      const routerCandidates = [
        router?.id,
        router?.nas_identifier,
        router?.identity,
        router?.name,
      ]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean);
      return routerCandidates.some((token) => candidates.has(token));
    }) || selectedRouterRecord || null;

    if (mappedRouter && hasOperationalCoordinates(mappedRouter.latitude, mappedRouter.longitude)) {
      return {
        routerId: String(mappedRouter.id || '').trim(),
        latitude: Number(mappedRouter.latitude),
        longitude: Number(mappedRouter.longitude),
      };
    }

    if (hasOperationalCoordinates(selectedNasRecord?.latitude, selectedNasRecord?.longitude)) {
      return {
        routerId: String(mappedRouter?.id || selectedRouterId || '').trim(),
        latitude: Number(selectedNasRecord.latitude),
        longitude: Number(selectedNasRecord.longitude),
      };
    }

    if (hasOperationalCoordinates(selectedRow?.latitude, selectedRow?.longitude)) {
      return {
        routerId: String(mappedRouter?.id || selectedRouterId || '').trim(),
        latitude: Number(selectedRow.latitude),
        longitude: Number(selectedRow.longitude),
      };
    }

    return {
      routerId: String(mappedRouter?.id || selectedRouterId || '').trim(),
      latitude: null,
      longitude: null,
    };
  }, [selectedRow, selectedNasRecord, routerCatalog, selectedRouterRecord, selectedRouterId]);

  const topologyLink = useMemo(() => {
    if (!selectedRow) return '/topology?mode=live';
    const mode = ['olt', 'odp'].includes(selectedRow.type) ? 'plan' : 'live';
    const params = new URLSearchParams();
    params.set('mode', mode);
    params.set('sourceType', String(selectedRow.type));
    params.set('sourceId', String(selectedRow.id));
    if (selectedRow.site_id && selectedRow.site_id !== '-') params.set('siteId', String(selectedRow.site_id));
    if (selectedRow.type === 'nas') {
      const deepLinkRouterId = topologyRouterContext.routerId || (selectedRouterContext.isValid ? selectedRouterId : '');
      if (deepLinkRouterId) params.set('routerId', String(deepLinkRouterId));
      if (hasOperationalCoordinates(topologyRouterContext.latitude, topologyRouterContext.longitude)) {
        params.set('latitude', String(topologyRouterContext.latitude));
        params.set('longitude', String(topologyRouterContext.longitude));
      } else if (hasOperationalCoordinates(selectedSiteRecord?.latitude, selectedSiteRecord?.longitude)) {
        params.set('latitude', String(selectedSiteRecord.latitude));
        params.set('longitude', String(selectedSiteRecord.longitude));
      }
      params.set('openDetail', '1');
    }
    return `/topology?${params.toString()}`;
  }, [selectedRow, topologyRouterContext, selectedRouterContext.isValid, selectedRouterId, selectedSiteRecord]);

  const loadRouterCatalog = useCallback(async () => {
    const headers = getHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${API}/routers`, { headers });
      setRouterCatalog(Array.isArray(response?.data) ? response.data : []);
    } catch (loadError) {
      setRouterCatalog([]);
      toast.error(loadError?.response?.data?.detail || 'Gagal memuat katalog router.');
    }
  }, [getHeaders]);

  const loadSites = useCallback(async () => {
    const headers = getHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${API}/sites`, { headers });
      setSites(Array.isArray(response?.data) ? response.data : []);
    } catch (loadError) {
      setSites([]);
      toast.error(loadError?.response?.data?.detail || 'Gagal memuat master site.');
    }
  }, [getHeaders]);

  const loadRouterRelations = useCallback(async (routerId) => {
    const targetRouterId = String(routerId || '').trim();
    if (!targetRouterId) {
      setRouterRelations([]);
      setRelationsError('');
      return;
    }
    const headers = getHeaders();
    if (!headers) return;
    try {
      setRelationsLoading(true);
      setRelationsError('');
      const response = await axios.get(`${API}/routers/${encodeURIComponent(targetRouterId)}/relations`, { headers });
      setRouterRelations(Array.isArray(response?.data) ? response.data : []);
    } catch (loadError) {
      setRouterRelations([]);
      const rawDetail = String(loadError?.response?.data?.detail || '').trim();
      if (!rawDetail) {
        setRelationsError('Gagal memuat relasi router. Coba refresh beberapa saat lagi.');
      } else if (/not found|tidak ditemukan/i.test(rawDetail)) {
        setRelationsError('Router tidak ditemukan di sumber data terbaru. Pilih ulang router lalu coba lagi.');
      } else {
        setRelationsError(rawDetail);
      }
    } finally {
      setRelationsLoading(false);
    }
  }, [getHeaders]);

  const closeRelationEditor = useCallback(() => {
    setRelationEditorOpen(false);
    setRelationEditorMode('create');
    setEditingRelationId('');
    setRelationForm({ ...DEFAULT_RELATION_FORM });
  }, []);

  const openCreateRelation = useCallback(() => {
    if (!selectedRouterContext.isValid || !selectedRouterId) {
      toast.error(selectedRouterContext.reason || 'Router sumber tidak valid untuk relasi.');
      return;
    }
    setRelationEditorMode('create');
    setEditingRelationId('');
    setRelationForm({
      ...DEFAULT_RELATION_FORM,
      from_router_id: selectedRouterId,
    });
    setRelationEditorOpen(true);
  }, [selectedRouterContext, selectedRouterId]);

  const openEditRelation = useCallback((relation) => {
    if (!relation) return;
    setRelationEditorMode('edit');
    setEditingRelationId(String(relation.id || ''));
    setRelationForm({
      from_router_id: String(relation.from_router_id || ''),
      to_router_id: String(relation.to_router_id || ''),
      relation_type: String(relation.relation_type || 'primary').toLowerCase(),
      priority_order: relation.priority_order === null || relation.priority_order === undefined ? '' : String(relation.priority_order),
      is_active: Boolean(relation.is_active ?? true),
      notes: String(relation.notes || ''),
    });
    setRelationEditorOpen(true);
  }, []);

  const submitRelation = useCallback(async (event) => {
    event.preventDefault();
    if (relationFormError) {
      toast.error(relationFormError);
      return;
    }
    const headers = getHeaders();
    if (!headers) return;
    const payload = {
      from_router_id: String(relationForm.from_router_id || '').trim(),
      to_router_id: String(relationForm.to_router_id || '').trim(),
      relation_type: String(relationForm.relation_type || 'primary').trim().toLowerCase(),
      priority_order: String(relationForm.priority_order ?? '').trim() === '' ? null : Number(relationForm.priority_order),
      is_active: Boolean(relationForm.is_active),
      notes: String(relationForm.notes || '').trim() || null,
    };
    try {
      setRelationSubmitting(true);
      if (relationEditorMode === 'edit' && editingRelationId) {
        await axios.put(`${API}/router-relations/${encodeURIComponent(editingRelationId)}`, payload, { headers });
        toast.success('Relasi router diperbarui.');
      } else {
        await axios.post(`${API}/router-relations`, payload, { headers });
        toast.success('Relasi router ditambahkan.');
      }
      closeRelationEditor();
      await loadRouterRelations(selectedRouterId);
    } catch (submitError) {
      toast.error(submitError?.response?.data?.detail || 'Gagal menyimpan relasi router.');
    } finally {
      setRelationSubmitting(false);
    }
  }, [relationForm, relationFormError, getHeaders, relationEditorMode, editingRelationId, closeRelationEditor, loadRouterRelations, selectedRouterId]);

  const toggleRelationActive = useCallback(async (relation) => {
    if (!relation?.id) return;
    const headers = getHeaders();
    if (!headers) return;
    try {
      await axios.put(`${API}/router-relations/${encodeURIComponent(String(relation.id))}`, {
        is_active: !Boolean(relation.is_active),
      }, { headers });
      toast.success(Boolean(relation.is_active) ? 'Relasi dinonaktifkan.' : 'Relasi diaktifkan.');
      await loadRouterRelations(selectedRouterId);
    } catch (toggleError) {
      toast.error(toggleError?.response?.data?.detail || 'Gagal memperbarui status relasi.');
    }
  }, [getHeaders, loadRouterRelations, selectedRouterId]);

  const handleDeleteRelation = useCallback(async (relation) => {
    if (!relation?.id) return;
    if (!window.confirm('Hapus relasi router ini?')) return;
    const headers = getHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${API}/router-relations/${encodeURIComponent(String(relation.id))}`, { headers });
      toast.success('Relasi router dihapus.');
      await loadRouterRelations(selectedRouterId);
    } catch (deleteError) {
      toast.error(deleteError?.response?.data?.detail || 'Gagal menghapus relasi router.');
    }
  }, [getHeaders, loadRouterRelations, selectedRouterId]);

  const handleRefreshAll = useCallback(async () => {
    await refresh();
    await loadRouterCatalog();
    await loadSites();
    if (selectedRouterId) {
      await loadRouterRelations(selectedRouterId);
    }
  }, [refresh, loadRouterCatalog, loadSites, loadRouterRelations, selectedRouterId]);

  React.useEffect(() => {
    loadRouterCatalog();
    loadSites();
  }, [loadRouterCatalog, loadSites]);

  React.useEffect(() => {
    loadRouterRelations(selectedRouterId);
    closeRelationEditor();
    setShowAllRelations(false);
  }, [selectedRouterId, loadRouterRelations, closeRelationEditor]);

  const submitOlt = async (event) => {
    event.preventDefault();
    if (!String(oltForm.name || '').trim()) {
      toast.error('Nama OLT wajib diisi.');
      return;
    }
    if (oltCoordinateError) {
      toast.error(oltCoordinateError);
      return;
    }

    const headers = getHeaders();
    if (!headers) return;
    const payload = {
      name: String(oltForm.name || '').trim(),
      code: String(oltForm.code || '').trim() || null,
      router_id: String(oltForm.router_id || '').trim() || null,
      kriteria: String(oltForm.kriteria || 'live').toLowerCase() === 'plan' ? 'plan' : 'live',
      location: String(oltForm.location || '').trim() || null,
      latitude: oltForm.latitude === '' ? null : Number(oltForm.latitude),
      longitude: oltForm.longitude === '' ? null : Number(oltForm.longitude),
      x: Number(oltForm.x || DEFAULT_OLT_FORM.x),
      y: Number(oltForm.y || DEFAULT_OLT_FORM.y),
    };

    try {
      setSubmitting(true);
      if (editorMode === 'edit' && editingId) {
        await axios.put(`${API}/ftth/olts/${encodeURIComponent(editingId)}`, payload, { headers });
        toast.success('OLT berhasil diperbarui.');
      } else {
        await axios.post(`${API}/ftth/olts`, payload, { headers });
        toast.success('OLT berhasil ditambahkan.');
      }
      closeEditor();
      await refresh();
    } catch (submitError) {
      toast.error(submitError?.response?.data?.detail || 'Gagal menyimpan OLT.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitOdp = async (event) => {
    event.preventDefault();
    if (!String(odpForm.name || '').trim()) {
      toast.error('Nama ODP wajib diisi.');
      return;
    }
    if (!String(odpForm.olt_id || '').trim()) {
      toast.error('Pilih OLT untuk ODP.');
      return;
    }
    if (odpCoordinateError) {
      toast.error(odpCoordinateError);
      return;
    }

    const headers = getHeaders();
    if (!headers) return;
    const payload = {
      name: String(odpForm.name || '').trim(),
      code: String(odpForm.code || '').trim() || null,
      router_id: String(odpForm.router_id || '').trim() || null,
      kriteria: String(odpForm.kriteria || 'live').toLowerCase() === 'plan' ? 'plan' : 'live',
      olt_id: String(odpForm.olt_id || '').trim() || null,
      address: String(odpForm.address || '').trim() || null,
      latitude: odpForm.latitude === '' ? null : Number(odpForm.latitude),
      longitude: odpForm.longitude === '' ? null : Number(odpForm.longitude),
      x: Number(odpForm.x || DEFAULT_ODP_FORM.x),
      y: Number(odpForm.y || DEFAULT_ODP_FORM.y),
    };

    try {
      setSubmitting(true);
      if (editorMode === 'edit' && editingId) {
        await axios.put(`${API}/ftth/odps/${encodeURIComponent(editingId)}`, payload, { headers });
        toast.success('ODP berhasil diperbarui.');
      } else {
        await axios.post(`${API}/ftth/odps`, payload, { headers });
        toast.success('ODP berhasil ditambahkan.');
      }
      closeEditor();
      await refresh();
    } catch (submitError) {
      toast.error(submitError?.response?.data?.detail || 'Gagal menyimpan ODP.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitRouter = async (event) => {
    event.preventDefault();
    if (!String(routerForm.name || '').trim()) {
      toast.error('Nama Router wajib diisi.');
      return;
    }
    if (!String(routerForm.site_id || '').trim()) {
      toast.error('Site router wajib dipilih.');
      return;
    }
    if (routerCoordinateError) {
      toast.error(routerCoordinateError);
      return;
    }
    const headers = getHeaders();
    if (!headers) return;
    const payload = {
      site_id: String(routerForm.site_id || '').trim(),
      name: String(routerForm.name || '').trim(),
      identity: String(routerForm.identity || '').trim() || null,
      mgmt_ip: String(routerForm.mgmt_ip || '').trim() || null,
      nas_identifier: String(routerForm.nas_identifier || '').trim() || null,
      router_type: String(routerForm.router_type || 'pppoe').trim().toLowerCase(),
      router_role: String(routerForm.router_role || 'distribution').trim().toLowerCase(),
      router_label: String(routerForm.router_label || '').trim() || null,
      priority_order: String(routerForm.priority_order ?? '').trim() === '' ? null : Number(routerForm.priority_order),
      notes: String(routerForm.notes || '').trim() || null,
      status: String(routerForm.status || 'active').trim().toLowerCase(),
      latitude: routerForm.latitude === '' ? null : Number(routerForm.latitude),
      longitude: routerForm.longitude === '' ? null : Number(routerForm.longitude),
    };
    try {
      setSubmitting(true);
      if (editorMode === 'edit' && editingId) {
        await axios.put(`${API}/routers/${encodeURIComponent(editingId)}`, payload, { headers });
        toast.success('Router berhasil diperbarui.');
      } else {
        await axios.post(`${API}/routers`, payload, { headers });
        toast.success('Router berhasil ditambahkan.');
      }
      closeEditor();
      await Promise.all([refresh(), loadRouterCatalog()]);
    } catch (submitError) {
      toast.error(submitError?.response?.data?.detail || 'Gagal menyimpan router.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitSite = async (event) => {
    event.preventDefault();
    if (!String(siteForm.code || '').trim()) {
      toast.error('Kode Site wajib diisi.');
      return;
    }
    if (!String(siteForm.name || '').trim()) {
      toast.error('Nama Site wajib diisi.');
      return;
    }
    if (siteCoordinateError) {
      toast.error(siteCoordinateError);
      return;
    }
    const headers = getHeaders();
    if (!headers) return;
    const payload = {
      code: String(siteForm.code || '').trim().toUpperCase(),
      name: String(siteForm.name || '').trim(),
      address: String(siteForm.address || '').trim() || null,
      description: String(siteForm.description || '').trim() || null,
      timezone: String(siteForm.timezone || 'Asia/Makassar').trim() || 'Asia/Makassar',
      status: String(siteForm.status || 'active').trim().toLowerCase(),
      latitude: siteForm.latitude === '' ? null : Number(siteForm.latitude),
      longitude: siteForm.longitude === '' ? null : Number(siteForm.longitude),
    };
    try {
      setSubmitting(true);
      if (editorMode === 'edit' && editingId) {
        await axios.put(`${API}/sites/${encodeURIComponent(editingId)}`, payload, { headers });
        toast.success('Site berhasil diperbarui.');
      } else {
        await axios.post(`${API}/sites`, payload, { headers });
        toast.success('Site berhasil ditambahkan.');
      }
      closeEditor();
      await loadSites();
    } catch (submitError) {
      toast.error(submitError?.response?.data?.detail || 'Gagal menyimpan site.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRouter = async (routerId) => {
    if (!routerId) return;
    if (!window.confirm('Hapus router ini?')) return;
    const headers = getHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${API}/routers/${encodeURIComponent(String(routerId))}`, { headers });
      toast.success('Router dihapus.');
      await Promise.all([refresh(), loadRouterCatalog()]);
    } catch (deleteError) {
      toast.error(deleteError?.response?.data?.detail || 'Gagal menghapus router.');
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!siteId) return;
    if (!window.confirm('Hapus site ini?')) return;
    const headers = getHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${API}/sites/${encodeURIComponent(String(siteId))}`, { headers });
      toast.success('Site dihapus.');
      await loadSites();
    } catch (deleteError) {
      toast.error(deleteError?.response?.data?.detail || 'Gagal menghapus site.');
    }
  };

  const handleDeleteOlt = async (oltId) => {
    const linkedOdpCount = odps.filter((item) => String(item.olt_id || '') === String(oltId)).length;
    if (linkedOdpCount > 0) {
      toast.error(`OLT tidak bisa dihapus karena masih punya ${linkedOdpCount} ODP terkait.`);
      return;
    }

    if (!window.confirm('Hapus OLT ini?')) return;
    const headers = getHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${API}/ftth/olts/${encodeURIComponent(oltId)}`, { headers });
      toast.success('OLT dihapus.');
      await refresh();
    } catch (deleteError) {
      toast.error(deleteError?.response?.data?.detail || 'Gagal menghapus OLT.');
    }
  };

  const handleDeleteOdp = async (odpId) => {
    const linkedCustomerCount = customers.filter((item) => String(item.linked_odp_id || item.odp_id || '') === String(odpId)).length;
    if (linkedCustomerCount > 0) {
      toast.error(`ODP tidak bisa dihapus karena masih dipakai ${linkedCustomerCount} pelanggan.`);
      return;
    }

    if (!window.confirm('Hapus ODP ini?')) return;
    const headers = getHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${API}/ftth/odps/${encodeURIComponent(odpId)}`, { headers });
      toast.success('ODP dihapus.');
      await refresh();
    } catch (deleteError) {
      toast.error(deleteError?.response?.data?.detail || 'Gagal menghapus ODP.');
    }
  };

  const columns = [
    { key: 'id', label: 'Node ID' },
    { key: 'type', label: 'Type' },
    { key: 'name', label: 'Name' },
    { key: 'site', label: 'Site' },
    { key: 'health', label: 'Health' },
    { key: 'capacity', label: 'Capacity' },
    { key: 'actions', label: 'Aksi' },
  ];

  const handleCreateTicket = async () => {
    if (!selectedRow) return;
    const headers = getHeaders();
    if (!headers) return;
    const title = String(ticketDraft.title || '').trim();
    if (!title) {
      toast.error('Judul ticket wajib diisi.');
      return;
    }
    setTicketSaving(true);
    try {
      const sourceType = selectedRow.type === 'nas' ? 'router' : selectedRow.type === 'olt' || selectedRow.type === 'odp' ? 'site' : 'manual';
      const firstIncidentId = relatedIncidents[0]?.id;
      const created = await createTicket({
        headers,
        payload: {
          title,
          description: ticketDraft.description,
          priority: ticketDraft.priority,
          assigned_to: ticketDraft.assigned_to || undefined,
          source_type: sourceType,
          source_id: String(selectedRow.id || ''),
          router_id: selectedRow.type === 'nas' ? String(selectedRow.id || '') : undefined,
          node_id: String(selectedRow.id || ''),
          site_id: String(selectedRow.site_id || '') || undefined,
          incident_id: firstIncidentId || undefined,
          customer_id: relatedCustomers[0]?.id || undefined,
        },
      });
      created.warnings.forEach((warning) => toast.warning(warning));
      toast.success(`Ticket ${created.item.ticketCode || created.item.id} dibuat.`);
      setTicketDraft({ title: '', description: '', priority: 'medium', assigned_to: '' });
      await refresh();
    } catch (err) {
      toast.error(err?.message || 'Gagal membuat ticket.');
    } finally {
      setTicketSaving(false);
    }
  };

  const handleAddTicketUpdate = async (ticketId) => {
    const headers = getHeaders();
    if (!headers || !ticketId) return;
    const note = String(ticketUpdateNote || '').trim();
    if (!note) {
      toast.error('Catatan update belum diisi.');
      return;
    }
    setTicketSaving(true);
    try {
      await appendTicketUpdate({ headers, ticketId, note });
      setTicketUpdateNote('');
      toast.success('Update ticket tersimpan.');
      await refresh();
    } catch (err) {
      toast.error(err?.message || 'Gagal menambah update ticket.');
    } finally {
      setTicketSaving(false);
    }
  };

  return (
    <AppShell sidebarItems={commandSidebarItems} activePathname="/infrastructure" activeSearch="" initialTab="overview">
      <PageContainer>
        <PageHeader
          eyebrow="Infrastructure"
          title="Node & Device Investigation"
          subtitle="Inventory infrastruktur untuk diagnosis cepat: health node, relasi layanan, kapasitas, dan dampak gangguan."
          action={(
            <UIButton variant="ghost" onClick={handleRefreshAll} disabled={loading || relationsLoading}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </UIButton>
          )}
        />

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Cari node id / nama / site..."
            className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 md:max-w-md"
          />
          <select
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value)}
            className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200"
          >
            <option value="all">Semua Type</option>
            <option value="nas">Router/NAS</option>
            <option value="olt">OLT</option>
            <option value="odp">ODP</option>
          </select>
          <UIButton variant="subtle" onClick={() => openCreateOlt()}>
            <Plus className="h-4 w-4" />
            Tambah OLT
          </UIButton>
          <UIButton variant="subtle" onClick={() => openCreateOdp()}>
            <Plus className="h-4 w-4" />
            Tambah ODP
          </UIButton>
          <UIButton variant="subtle" onClick={() => openCreateRouter()}>
            <Plus className="h-4 w-4" />
            Tambah Router
          </UIButton>
          <UIButton variant="ghost" onClick={() => openCreateSite()}>
            <Plus className="h-4 w-4" />
            Tambah Site
          </UIButton>
          <span className="ml-auto text-xs text-slate-500">
            Last update: {lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}
          </span>
        </div>

        {editorOpen ? (
          <SectionContainer className="mb-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white">
                  {editorMode === 'edit'
                    ? `Edit ${editorType === 'site' ? 'Site' : editorType === 'router' ? 'Router' : editorType === 'olt' ? 'OLT' : 'ODP'}`
                    : `Tambah ${editorType === 'site' ? 'Site' : editorType === 'router' ? 'Router' : editorType === 'olt' ? 'OLT' : 'ODP'}`}
                </p>
                <p className="text-xs text-slate-400">
                  Form CRUD diarahkan ke endpoint FTTH existing tanpa mengubah kontrak backend.
                </p>
              </div>
              <UIButton variant="ghost" onClick={closeEditor}>
                <X className="h-4 w-4" />
                Tutup
              </UIButton>
            </div>
            {editorContextHint ? (
              <div className="rounded-lg border border-cyan-500/25 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-100">
                <p>{editorContextHint}</p>
                <div className="mt-1.5 space-y-0.5 text-[11px] text-cyan-50/90">
                  {editorContextMeta?.site ? <p>Site aktif: {editorContextMeta.site}</p> : null}
                  {(editorContextMeta?.sourceType || editorContextMeta?.sourceId) ? <p>Node sumber: {editorContextMeta?.sourceType || '-'} {editorContextMeta?.sourceId ? `(${editorContextMeta.sourceId})` : ''}</p> : null}
                  <p>{editorContextMeta?.hasCoordinates ? 'Koordinat awal tersedia dari topology.' : 'Koordinat awal belum tersedia dari topology.'}</p>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-xs text-slate-400">
                Tidak ada konteks topology aktif. Gunakan lokasi saat ini, isi manual, atau pilih titik di peta.
              </div>
            )}
            <datalist id="infra-site-suggestions">
              {siteSuggestions.map((site) => (
                <option key={site} value={site} />
              ))}
            </datalist>

            {editorType === 'olt' ? (
              <form className="grid gap-2 md:grid-cols-2" onSubmit={submitOlt}>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Nama OLT</span>
                  <input value={oltForm.name} onChange={(event) => setOltForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Contoh: OLT-CORE-01" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Kode OLT <span className="text-slate-500">(opsional)</span></span>
                  <input value={oltForm.code} onChange={(event) => setOltForm((prev) => ({ ...prev, code: event.target.value }))} placeholder="Contoh: MKS-OLT-01" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Router/NAS Terkait <span className="text-slate-500">(opsional)</span></span>
                  <select value={oltForm.router_id} onChange={(event) => setOltForm((prev) => ({ ...prev, router_id: event.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
                    <option value="">Tidak dipilih</option>
                    {nasRouters.map((item) => (
                      <option key={item.id || item.router_id} value={String(item.id || item.router_id)}>
                        {item.name || item.nas_identifier || item.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Kriteria Infrastruktur</span>
                  <select value={oltForm.kriteria} onChange={(event) => setOltForm((prev) => ({ ...prev, kriteria: event.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
                    <option value="live">Live</option>
                    <option value="plan">Plan</option>
                  </select>
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs text-slate-300">Site / Lokasi OLT</span>
                  <input list="infra-site-suggestions" value={oltForm.location} onChange={(event) => handleSiteInputChange('olt', event.target.value)} placeholder="Masukkan atau pilih site" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  <p className="text-[11px] text-slate-500">Tips: pilih site dari saran agar map otomatis memusat ke koordinat site (jika tersedia).</p>
                </label>
                <div className="md:col-span-2 flex flex-wrap gap-1.5">
                  {siteSuggestions.slice(0, 6).map((site) => (
                    <button
                      key={`olt-site-${site}`}
                      type="button"
                      onClick={() => handleSiteInputChange('olt', site)}
                      className="rounded-md border border-white/15 bg-[#0e1a2d] px-2 py-1 text-[11px] text-slate-200 hover:bg-[#14243d]"
                    >
                      {site}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="space-y-1">
                    <span className="text-xs text-slate-300">Latitude <span className="text-slate-500">(opsional, -90 s/d 90)</span></span>
                    <input value={oltForm.latitude} onChange={(event) => setOltForm((prev) => ({ ...prev, latitude: event.target.value }))} placeholder="-3.432400" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs text-slate-300">Longitude <span className="text-slate-500">(opsional, -180 s/d 180)</span></span>
                    <input value={oltForm.longitude} onChange={(event) => setOltForm((prev) => ({ ...prev, longitude: event.target.value }))} placeholder="119.343000" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  </label>
                </div>
                {oltCoordinateError ? (
                  <p className="md:col-span-2 text-xs text-amber-300">{oltCoordinateError}</p>
                ) : null}
                {!oltCoordinateError && oltCoordinateWarning ? (
                  <p className="md:col-span-2 text-xs text-amber-200">{oltCoordinateWarning}</p>
                ) : null}
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  <UIButton type="button" variant="ghost" onClick={() => handleGetCurrentLocation('olt')}>
                    Gunakan lokasi saat ini
                  </UIButton>
                  <UIButton type="button" variant="ghost" onClick={() => setShowOltMapPicker((prev) => !prev)}>
                    {showOltMapPicker ? 'Sembunyikan peta' : 'Tampilkan peta'}
                  </UIButton>
                </div>
                {showOltMapPicker ? (
                  <div className="md:col-span-2 space-y-1">
                    <LeafletPinPicker
                      title="Lokasi OLT"
                      latitude={oltForm.latitude}
                      longitude={oltForm.longitude}
                      fallbackCenter={{
                        latitude: hasOperationalCoordinates(oltForm.latitude, oltForm.longitude) ? Number(oltForm.latitude) : -3.4324,
                        longitude: hasOperationalCoordinates(oltForm.latitude, oltForm.longitude) ? Number(oltForm.longitude) : 119.3430,
                      }}
                      onChange={(latitude, longitude) => handleMapLocationChange('olt', latitude, longitude)}
                      heightClass="h-40"
                    />
                    <p className="text-xs text-slate-400">
                      Koordinat aktif: {hasOperationalCoordinates(oltForm.latitude, oltForm.longitude) ? `${Number(oltForm.latitude).toFixed(6)}, ${Number(oltForm.longitude).toFixed(6)}` : 'belum valid operasional'}
                    </p>
                  </div>
                ) : null}
                <div className="md:col-span-2 rounded-lg border border-white/10 bg-[#0a1322]">
                  <button
                    type="button"
                    onClick={() => setShowOltLegacyFields((prev) => !prev)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-xs text-slate-300"
                  >
                    <span>Lanjutan / Legacy</span>
                    <span>{showOltLegacyFields ? 'Sembunyikan' : 'Tampilkan'}</span>
                  </button>
                  {showOltLegacyFields ? (
                    <div className="grid grid-cols-2 gap-2 border-t border-white/10 px-3 py-2">
                      <label className="space-y-1">
                        <span className="text-xs text-slate-300">Posisi Kanvas X <span className="text-slate-500">(opsional/legacy)</span></span>
                        <input value={oltForm.x} onChange={(event) => setOltForm((prev) => ({ ...prev, x: event.target.value }))} placeholder="120" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-300">Posisi Kanvas Y <span className="text-slate-500">(opsional/legacy)</span></span>
                        <input value={oltForm.y} onChange={(event) => setOltForm((prev) => ({ ...prev, y: event.target.value }))} placeholder="140" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                      </label>
                    </div>
                  ) : null}
                </div>
                <div className="md:col-span-2 flex justify-end gap-2 pt-1">
                  <UIButton type="button" variant="ghost" onClick={closeEditor}>Batal</UIButton>
                  <UIButton type="submit" variant="subtle" disabled={submitting || Boolean(oltCoordinateError)}>{submitting ? 'Menyimpan...' : 'Simpan OLT'}</UIButton>
                </div>
              </form>
            ) : editorType === 'odp' ? (
              <form className="grid gap-2 md:grid-cols-2" onSubmit={submitOdp}>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Nama ODP</span>
                  <input value={odpForm.name} onChange={(event) => setOdpForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Contoh: ODP-MKS-12" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Kode ODP <span className="text-slate-500">(opsional)</span></span>
                  <input value={odpForm.code} onChange={(event) => setOdpForm((prev) => ({ ...prev, code: event.target.value }))} placeholder="Contoh: MKS-ODP-12" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Relasi OLT</span>
                  <select value={odpForm.olt_id} onChange={(event) => setOdpForm((prev) => ({ ...prev, olt_id: event.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
                    <option value="">Pilih OLT</option>
                    {olts.map((item) => (
                      <option key={item.id} value={String(item.id)}>
                        {item.name || item.code || item.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Router/NAS Terkait <span className="text-slate-500">(opsional)</span></span>
                  <select value={odpForm.router_id} onChange={(event) => setOdpForm((prev) => ({ ...prev, router_id: event.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
                    <option value="">Tidak dipilih</option>
                    {nasRouters.map((item) => (
                      <option key={item.id || item.router_id} value={String(item.id || item.router_id)}>
                        {item.name || item.nas_identifier || item.id}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Kriteria Infrastruktur</span>
                  <select value={odpForm.kriteria} onChange={(event) => setOdpForm((prev) => ({ ...prev, kriteria: event.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
                    <option value="live">Live</option>
                    <option value="plan">Plan</option>
                  </select>
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs text-slate-300">Site / Alamat ODP</span>
                  <input list="infra-site-suggestions" value={odpForm.address} onChange={(event) => handleSiteInputChange('odp', event.target.value)} placeholder="Masukkan atau pilih site/alamat" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  <p className="text-[11px] text-slate-500">Tips: gunakan saran site untuk memudahkan penentuan titik ODP.</p>
                </label>
                <div className="md:col-span-2 flex flex-wrap gap-1.5">
                  {siteSuggestions.slice(0, 6).map((site) => (
                    <button
                      key={`odp-site-${site}`}
                      type="button"
                      onClick={() => handleSiteInputChange('odp', site)}
                      className="rounded-md border border-white/15 bg-[#0e1a2d] px-2 py-1 text-[11px] text-slate-200 hover:bg-[#14243d]"
                    >
                      {site}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="space-y-1">
                    <span className="text-xs text-slate-300">Latitude <span className="text-slate-500">(opsional, -90 s/d 90)</span></span>
                    <input value={odpForm.latitude} onChange={(event) => setOdpForm((prev) => ({ ...prev, latitude: event.target.value }))} placeholder="-3.432400" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs text-slate-300">Longitude <span className="text-slate-500">(opsional, -180 s/d 180)</span></span>
                    <input value={odpForm.longitude} onChange={(event) => setOdpForm((prev) => ({ ...prev, longitude: event.target.value }))} placeholder="119.343000" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  </label>
                </div>
                {odpCoordinateError ? (
                  <p className="md:col-span-2 text-xs text-amber-300">{odpCoordinateError}</p>
                ) : null}
                {!odpCoordinateError && odpCoordinateWarning ? (
                  <p className="md:col-span-2 text-xs text-amber-200">{odpCoordinateWarning}</p>
                ) : null}
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  <UIButton type="button" variant="ghost" onClick={() => handleGetCurrentLocation('odp')}>
                    Gunakan lokasi saat ini
                  </UIButton>
                  <UIButton type="button" variant="ghost" onClick={() => setShowOdpMapPicker((prev) => !prev)}>
                    {showOdpMapPicker ? 'Sembunyikan peta' : 'Tampilkan peta'}
                  </UIButton>
                </div>
                {showOdpMapPicker ? (
                  <div className="md:col-span-2 space-y-1">
                    <LeafletPinPicker
                      title="Lokasi ODP"
                      latitude={odpForm.latitude}
                      longitude={odpForm.longitude}
                      fallbackCenter={{
                        latitude: hasOperationalCoordinates(odpForm.latitude, odpForm.longitude) ? Number(odpForm.latitude) : -3.4324,
                        longitude: hasOperationalCoordinates(odpForm.latitude, odpForm.longitude) ? Number(odpForm.longitude) : 119.3430,
                      }}
                      onChange={(latitude, longitude) => handleMapLocationChange('odp', latitude, longitude)}
                      heightClass="h-40"
                    />
                    <p className="text-xs text-slate-400">
                      Koordinat aktif: {hasOperationalCoordinates(odpForm.latitude, odpForm.longitude) ? `${Number(odpForm.latitude).toFixed(6)}, ${Number(odpForm.longitude).toFixed(6)}` : 'belum valid operasional'}
                    </p>
                  </div>
                ) : null}
                <div className="md:col-span-2 rounded-lg border border-white/10 bg-[#0a1322]">
                  <button
                    type="button"
                    onClick={() => setShowOdpLegacyFields((prev) => !prev)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-xs text-slate-300"
                  >
                    <span>Lanjutan / Legacy</span>
                    <span>{showOdpLegacyFields ? 'Sembunyikan' : 'Tampilkan'}</span>
                  </button>
                  {showOdpLegacyFields ? (
                    <div className="grid grid-cols-2 gap-2 border-t border-white/10 px-3 py-2">
                      <label className="space-y-1">
                        <span className="text-xs text-slate-300">Posisi Kanvas X <span className="text-slate-500">(opsional/legacy)</span></span>
                        <input value={odpForm.x} onChange={(event) => setOdpForm((prev) => ({ ...prev, x: event.target.value }))} placeholder="450" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                      </label>
                      <label className="space-y-1">
                        <span className="text-xs text-slate-300">Posisi Kanvas Y <span className="text-slate-500">(opsional/legacy)</span></span>
                        <input value={odpForm.y} onChange={(event) => setOdpForm((prev) => ({ ...prev, y: event.target.value }))} placeholder="180" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                      </label>
                    </div>
                  ) : null}
                </div>
                <div className="md:col-span-2 flex justify-end gap-2 pt-1">
                  <UIButton type="button" variant="ghost" onClick={closeEditor}>Batal</UIButton>
                  <UIButton type="submit" variant="subtle" disabled={submitting || Boolean(odpCoordinateError)}>{submitting ? 'Menyimpan...' : 'Simpan ODP'}</UIButton>
                </div>
              </form>
            ) : editorType === 'router' ? (
              <form className="grid gap-2 md:grid-cols-2" onSubmit={submitRouter}>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Nama Router</span>
                  <input value={routerForm.name} onChange={(event) => setRouterForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Contoh: rb4011-loadbalancer" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">NAS Identifier <span className="text-slate-500">(opsional)</span></span>
                  <input value={routerForm.nas_identifier} onChange={(event) => setRouterForm((prev) => ({ ...prev, nas_identifier: event.target.value }))} placeholder="Contoh: gs-router" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Site Router</span>
                  <select
                    value={routerForm.site_id}
                    onChange={(event) => {
                      const siteValue = event.target.value;
                      setRouterForm((prev) => ({ ...prev, site_id: siteValue }));
                      const matchedCoordinates = resolveSiteCoordinates(siteValue, 'all');
                      if (matchedCoordinates) {
                        handleMapLocationChange('router', matchedCoordinates.latitude, matchedCoordinates.longitude);
                        setShowRouterMapPicker(true);
                      }
                    }}
                    className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200"
                  >
                    <option value="">Pilih site</option>
                    {sites.map((site) => (
                      <option key={site.id} value={String(site.id)}>{site.name} ({site.code || site.id})</option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Role Router</span>
                  <select value={routerForm.router_role} onChange={(event) => setRouterForm((prev) => ({ ...prev, router_role: event.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
                    <option value="uplink">uplink</option>
                    <option value="aggregation">aggregation</option>
                    <option value="load_balancer">load_balancer</option>
                    <option value="distribution">distribution</option>
                    <option value="backup">backup</option>
                    <option value="access">access</option>
                    <option value="service_edge">service_edge</option>
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Router Type</span>
                  <input value={routerForm.router_type} onChange={(event) => setRouterForm((prev) => ({ ...prev, router_type: event.target.value }))} placeholder="pppoe" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Status</span>
                  <select value={routerForm.status} onChange={(event) => setRouterForm((prev) => ({ ...prev, status: event.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                    <option value="maintenance">maintenance</option>
                  </select>
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs text-slate-300">Management IP <span className="text-slate-500">(opsional)</span></span>
                  <input value={routerForm.mgmt_ip} onChange={(event) => setRouterForm((prev) => ({ ...prev, mgmt_ip: event.target.value }))} placeholder="10.10.10.1" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs text-slate-300">Label Router <span className="text-slate-500">(opsional)</span></span>
                  <input value={routerForm.router_label} onChange={(event) => setRouterForm((prev) => ({ ...prev, router_label: event.target.value }))} placeholder="Metro Uplink" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <div className="grid grid-cols-2 gap-2 md:col-span-2">
                  <label className="space-y-1">
                    <span className="text-xs text-slate-300">Latitude</span>
                    <input value={routerForm.latitude} onChange={(event) => setRouterForm((prev) => ({ ...prev, latitude: event.target.value }))} placeholder="-5.189520" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs text-slate-300">Longitude</span>
                    <input value={routerForm.longitude} onChange={(event) => setRouterForm((prev) => ({ ...prev, longitude: event.target.value }))} placeholder="119.458910" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  </label>
                </div>
                {routerCoordinateError ? <p className="md:col-span-2 text-xs text-amber-300">{routerCoordinateError}</p> : null}
                {!routerCoordinateError && routerCoordinateWarning ? <p className="md:col-span-2 text-xs text-amber-200">{routerCoordinateWarning}</p> : null}
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  <UIButton type="button" variant="ghost" onClick={() => handleGetCurrentLocation('router')}>
                    Gunakan lokasi saat ini
                  </UIButton>
                  <UIButton type="button" variant="ghost" onClick={() => setShowRouterMapPicker((prev) => !prev)}>
                    {showRouterMapPicker ? 'Sembunyikan peta' : 'Tampilkan peta'}
                  </UIButton>
                </div>
                {showRouterMapPicker ? (
                  <div className="md:col-span-2 space-y-1">
                    <LeafletPinPicker
                      title="Lokasi Router"
                      latitude={routerForm.latitude}
                      longitude={routerForm.longitude}
                      fallbackCenter={{
                        latitude: hasOperationalCoordinates(routerForm.latitude, routerForm.longitude)
                          ? Number(routerForm.latitude)
                          : hasOperationalCoordinates(selectedSiteRecord?.latitude, selectedSiteRecord?.longitude)
                            ? Number(selectedSiteRecord.latitude)
                            : -3.4324,
                        longitude: hasOperationalCoordinates(routerForm.latitude, routerForm.longitude)
                          ? Number(routerForm.longitude)
                          : hasOperationalCoordinates(selectedSiteRecord?.latitude, selectedSiteRecord?.longitude)
                            ? Number(selectedSiteRecord.longitude)
                            : 119.3430,
                      }}
                      onChange={(latitude, longitude) => handleMapLocationChange('router', latitude, longitude)}
                      heightClass="h-40"
                    />
                    <p className="text-xs text-slate-400">
                      Klik peta atau geser pin untuk menentukan lokasi router.
                    </p>
                    <p className="text-xs text-slate-400">
                      Koordinat aktif: {hasOperationalCoordinates(routerForm.latitude, routerForm.longitude) ? `${Number(routerForm.latitude).toFixed(6)}, ${Number(routerForm.longitude).toFixed(6)}` : 'belum valid operasional'}
                    </p>
                  </div>
                ) : null}
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs text-slate-300">Notes <span className="text-slate-500">(opsional)</span></span>
                  <textarea value={routerForm.notes} onChange={(event) => setRouterForm((prev) => ({ ...prev, notes: event.target.value }))} rows={2} placeholder="Catatan operasional router" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <div className="md:col-span-2 flex justify-end gap-2 pt-1">
                  <UIButton type="button" variant="ghost" onClick={closeEditor}>Batal</UIButton>
                  <UIButton type="submit" variant="subtle" disabled={submitting || Boolean(routerCoordinateError)}>{submitting ? 'Menyimpan...' : 'Simpan Router'}</UIButton>
                </div>
              </form>
            ) : editorType === 'site' ? (
              <form className="grid gap-2 md:grid-cols-2" onSubmit={submitSite}>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Kode Site</span>
                  <input value={siteForm.code} onChange={(event) => setSiteForm((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))} placeholder="SITE-MKS-01" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Nama Site</span>
                  <input value={siteForm.name} onChange={(event) => setSiteForm((prev) => ({ ...prev, name: event.target.value }))} placeholder="Makassar Core" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs text-slate-300">Alamat <span className="text-slate-500">(opsional)</span></span>
                  <input value={siteForm.address} onChange={(event) => setSiteForm((prev) => ({ ...prev, address: event.target.value }))} placeholder="Jl. ..." className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <div className="grid grid-cols-2 gap-2 md:col-span-2">
                  <label className="space-y-1">
                    <span className="text-xs text-slate-300">Latitude <span className="text-slate-500">(opsional)</span></span>
                    <input value={siteForm.latitude} onChange={(event) => setSiteForm((prev) => ({ ...prev, latitude: event.target.value }))} placeholder="-5.189520" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  </label>
                  <label className="space-y-1">
                    <span className="text-xs text-slate-300">Longitude <span className="text-slate-500">(opsional)</span></span>
                    <input value={siteForm.longitude} onChange={(event) => setSiteForm((prev) => ({ ...prev, longitude: event.target.value }))} placeholder="119.458910" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                  </label>
                </div>
                {siteCoordinateError ? <p className="md:col-span-2 text-xs text-amber-300">{siteCoordinateError}</p> : null}
                {!siteCoordinateError && siteCoordinateWarning ? <p className="md:col-span-2 text-xs text-amber-200">{siteCoordinateWarning}</p> : null}
                <div className="md:col-span-2 flex flex-wrap gap-2">
                  <UIButton type="button" variant="ghost" onClick={() => handleGetCurrentLocation('site')}>
                    Gunakan lokasi saat ini
                  </UIButton>
                  <UIButton type="button" variant="ghost" onClick={() => setShowSiteMapPicker((prev) => !prev)}>
                    {showSiteMapPicker ? 'Sembunyikan peta' : 'Tampilkan peta'}
                  </UIButton>
                </div>
                {showSiteMapPicker ? (
                  <div className="md:col-span-2 space-y-1">
                    <LeafletPinPicker
                      title="Lokasi Site"
                      latitude={siteForm.latitude}
                      longitude={siteForm.longitude}
                      fallbackCenter={{
                        latitude: hasOperationalCoordinates(siteForm.latitude, siteForm.longitude) ? Number(siteForm.latitude) : -3.4324,
                        longitude: hasOperationalCoordinates(siteForm.latitude, siteForm.longitude) ? Number(siteForm.longitude) : 119.3430,
                      }}
                      onChange={(latitude, longitude) => handleMapLocationChange('site', latitude, longitude)}
                      heightClass="h-40"
                    />
                    <p className="text-xs text-slate-400">Klik peta atau geser pin untuk menentukan lokasi site.</p>
                    <p className="text-xs text-slate-400">
                      Koordinat aktif: {hasOperationalCoordinates(siteForm.latitude, siteForm.longitude) ? `${Number(siteForm.latitude).toFixed(6)}, ${Number(siteForm.longitude).toFixed(6)}` : 'belum valid operasional'}
                    </p>
                  </div>
                ) : null}
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Timezone</span>
                  <input value={siteForm.timezone} onChange={(event) => setSiteForm((prev) => ({ ...prev, timezone: event.target.value }))} placeholder="Asia/Makassar" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-300">Status</span>
                  <select value={siteForm.status} onChange={(event) => setSiteForm((prev) => ({ ...prev, status: event.target.value }))} className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-200">
                    <option value="active">active</option>
                    <option value="inactive">inactive</option>
                  </select>
                </label>
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs text-slate-300">Deskripsi <span className="text-slate-500">(opsional)</span></span>
                  <textarea value={siteForm.description} onChange={(event) => setSiteForm((prev) => ({ ...prev, description: event.target.value }))} rows={2} placeholder="Catatan lokasi/site" className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-3 py-2 text-sm text-slate-100" />
                </label>
                <div className="md:col-span-2 flex justify-end gap-2 pt-1">
                  <UIButton type="button" variant="ghost" onClick={closeEditor}>Batal</UIButton>
                  <UIButton type="submit" variant="subtle" disabled={submitting || Boolean(siteCoordinateError)}>{submitting ? 'Menyimpan...' : 'Simpan Site'}</UIButton>
                </div>
              </form>
            ) : (
              <div className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                Tipe editor tidak dikenal. Tutup form lalu buka ulang aksi CRUD yang diinginkan.
              </div>
            )}
          </SectionContainer>
        ) : null}

        {error ? (
          <SectionContainer className="mb-4 border border-rose-500/30 bg-rose-500/10 text-sm text-rose-200">
            {error}
          </SectionContainer>
        ) : null}

        <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_340px] 2xl:grid-cols-[minmax(0,1fr)_360px]">
          <SectionContainer className="min-w-0 overflow-hidden">
            <DataTable
              title="Infrastructure Inventory"
              subtitle="Pilih node untuk melihat dampak customer dan incident terkait."
              columns={columns}
              rows={filteredRows}
              loading={loading}
              rowKey="key"
              onRowClick={(row) => setSelectedKey(row.key)}
              emptyMessage="Tidak ada node pada scope ini. Sesuaikan type filter atau kata kunci."
              renderCell={(row, column) => {
                if (column.key === 'id') return <span className="font-semibold text-white">{row.id}</span>;
                if (column.key === 'type') return typeLabel[row.type] || row.type;
                if (column.key === 'name') return row.name;
                if (column.key === 'site') return row.site || '-';
                if (column.key === 'health') {
                  return (
                    <StatusBadge tone={mapInfrastructureStatusToTone(row.status)}>
                      {healthLabel(row.status)}
                    </StatusBadge>
                  );
                }
                if (column.key === 'capacity') return <span className="text-xs text-slate-300">{row.capacity}</span>;
                if (column.key === 'actions') {
                  if (!['nas', 'olt', 'odp'].includes(row.type)) return <span className="text-xs text-slate-500">-</span>;
                  return (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-[11px] text-slate-200 hover:bg-white/10"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (row.type === 'nas') {
                            const candidates = [
                              row.apiRouterId,
                              row.id,
                              row.nasIdentifier,
                            ].map((value) => String(value || '').trim()).filter(Boolean);
                            const target = routerCatalog.find((router) => {
                              const routerId = String(router.id || '').trim();
                              const nasIdentifier = String(router.nas_identifier || '').trim();
                              return candidates.includes(routerId) || (nasIdentifier && candidates.includes(nasIdentifier));
                            });
                            if (target) {
                              openEditRouter(target);
                            } else {
                              toast.error('Router belum tersedia di katalog. Sinkronisasi router diperlukan sebelum edit.');
                            }
                          } else if (row.type === 'olt') {
                            const target = olts.find((item) => String(item.id) === String(row.id));
                            if (target) openEditOlt(target);
                          } else {
                            const target = odps.find((item) => String(item.id) === String(row.id));
                            if (target) openEditOdp(target);
                          }
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 px-2 py-1 text-[11px] text-rose-200 hover:bg-rose-500/10"
                        onClick={(event) => {
                          event.stopPropagation();
                          if (row.type === 'nas') {
                            const candidates = [
                              row.apiRouterId,
                              row.id,
                              row.nasIdentifier,
                            ].map((value) => String(value || '').trim()).filter(Boolean);
                            const target = routerCatalog.find((router) => {
                              const routerId = String(router.id || '').trim();
                              const nasIdentifier = String(router.nas_identifier || '').trim();
                              return candidates.includes(routerId) || (nasIdentifier && candidates.includes(nasIdentifier));
                            });
                            if (target?.id) {
                              handleDeleteRouter(target.id);
                            } else {
                              toast.error('Router belum tersedia di katalog. Hapus dilakukan dari backend/router catalog.');
                            }
                          } else if (row.type === 'olt') {
                            handleDeleteOlt(row.id);
                          } else {
                            handleDeleteOdp(row.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Hapus
                      </button>
                    </div>
                  );
                }
                return '-';
              }}
            />
          </SectionContainer>

          <SectionContainer className="min-w-0 space-y-3 xl:w-[340px] 2xl:w-[360px]">
            {!selectedRow ? (
              <p className="rounded-lg border border-white/10 bg-[#0a1322] px-3 py-4 text-sm text-slate-400">
                Tidak ada node terpilih. Pilih baris dari inventory untuk melihat detail.
              </p>
            ) : (
              <>
                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-white">{selectedRow.name}</p>
                      <p className="text-xs text-slate-400">{typeLabel[selectedRow.type]} • {selectedRow.id}</p>
                    </div>
                    <StatusBadge tone={mapInfrastructureStatusToTone(selectedRow.status)}>
                      {healthLabel(selectedRow.status)}
                    </StatusBadge>
                  </div>
                  <p className="text-xs text-slate-300">Site: {selectedRow.site || '-'}</p>
                  <p className="text-xs text-slate-300">Capacity metric: {selectedRow.capacity}</p>
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Linked Entities</p>
                  <div className="space-y-1 text-xs text-slate-300">
                    <p>Related customers: <span className="text-white">{relatedCustomers.length}</span></p>
                    <p>Related incidents: <span className="text-white">{relatedIncidents.length}</span></p>
                    {selectedRow.type === 'odp' && selectedPrediction ? (
                      <p>
                        Capacity risk:
                        {' '}
                        <StatusBadge tone={selectedPrediction.risk.level === 'critical' ? 'danger' : selectedPrediction.risk.level === 'high' ? 'warn' : 'ok'}>
                          {riskLabel(selectedPrediction)}
                        </StatusBadge>
                      </p>
                    ) : (
                      <p className="text-slate-500">Relasi kapasitas belum lengkap untuk node ini.</p>
                    )}
                  </div>
                </div>

                {selectedRow.type === 'nas' ? (
                  <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Router Relations</p>
                      <div className="flex items-center gap-1.5">
                        <UIButton variant="ghost" onClick={() => loadRouterRelations(selectedRouterId)} disabled={relationsLoading || !selectedRouterContext.isValid}>
                          <RefreshCw className={`h-3.5 w-3.5 ${relationsLoading ? 'animate-spin' : ''}`} />
                          Reload
                        </UIButton>
                        <UIButton variant="subtle" onClick={openCreateRelation} disabled={!selectedRouterContext.isValid}>
                          <Plus className="h-3.5 w-3.5" />
                          Tambah Relasi
                        </UIButton>
                      </div>
                    </div>
                    {relationStats.total > 0 ? (
                      <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-300">
                        <span className="rounded-md border border-white/10 bg-[#101b2e] px-2 py-1">Total: {relationStats.total}</span>
                        <span className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-emerald-200">Aktif: {relationStats.active}</span>
                        <span className="rounded-md border border-slate-500/30 bg-slate-500/10 px-2 py-1 text-slate-300">Nonaktif: {relationStats.inactive}</span>
                      </div>
                    ) : null}
                    {relationsViewState === 'unavailable' ? (
                      <div className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2 text-xs text-slate-300">
                        <p className="font-medium text-white">Relasi router tidak tersedia</p>
                        <p className="mt-1 text-slate-400">{selectedRouterContext.reason}</p>
                      </div>
                    ) : null}
                    {relationsViewState === 'loading' ? (
                      <p className="text-xs text-slate-400">Memuat relasi router...</p>
                    ) : null}
                    {relationsViewState === 'error' ? (
                      <div className="mb-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-2.5 py-2 text-xs text-rose-200">
                        <p className="font-medium text-rose-100">Gagal memuat relasi router</p>
                        <p className="mt-0.5">{relationsError}</p>
                      </div>
                    ) : null}
                    {relationsViewState === 'empty' ? (
                      <div className="rounded-md border border-white/10 bg-[#101b2e] px-2.5 py-2 text-xs text-slate-300">
                        <p className="font-medium text-white">Belum ada relasi router</p>
                        <p className="mt-1 text-slate-400">Tambahkan jalur <span className="text-slate-200">primary</span>, <span className="text-slate-200">backup</span>, atau <span className="text-slate-200">balancing</span> untuk router ini.</p>
                        <p className="mt-1 text-slate-500">Gunakan relasi saat router ini punya koneksi utama/cadangan ke router lain.</p>
                        <div className="mt-2">
                          <UIButton variant="subtle" onClick={openCreateRelation} disabled={!selectedRouterContext.isValid}>
                            <Plus className="h-3.5 w-3.5" />
                            Tambah Relasi
                          </UIButton>
                        </div>
                      </div>
                    ) : null}
                    {relationsViewState === 'success' ? (
                      <div className="space-y-1.5">
                        {relationItemsToRender.map((relation) => {
                          const fromMeta = routerOptionById.get(String(relation.from_router_id || '').trim());
                          const toMeta = routerOptionById.get(String(relation.to_router_id || '').trim());
                          const relationTypeValue = String(relation.relation_type || '').toLowerCase();
                          const relationTypeLabel = RELATION_TYPE_OPTIONS.find((item) => item.value === relationTypeValue)?.label || String(relation.relation_type || '-');
                          return (
                            <div key={relation.id} className={`rounded-md border px-2.5 py-2 ${relation.is_active ? 'border-white/10 bg-[#13213a]' : 'border-slate-500/30 bg-[#111827] opacity-90'}`}>
                              <div className="mb-1 flex items-start justify-between gap-2">
                                <div>
                                  <p className="text-[12px] font-semibold text-white">
                                    {(relation.from_router_name || fromMeta?.name || relation.from_router_id)} ({fromMeta?.router_role || 'distribution'})
                                    {' '}→{' '}
                                    {(relation.to_router_name || toMeta?.name || relation.to_router_id)} ({toMeta?.router_role || 'distribution'})
                                    {' '}|{' '}
                                    {relationTypeLabel.toLowerCase()}
                                  </p>
                                  <p className="text-[11px] text-slate-400">
                                    {fromMeta?.site || '-'} → {toMeta?.site || '-'}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1">
                                  <StatusBadge tone={relation.is_active ? 'ok' : 'neutral'}>
                                    {relation.is_active ? 'ACTIVE' : 'INACTIVE'}
                                  </StatusBadge>
                                  <StatusBadge tone={relation.relation_type === 'backup' ? 'warn' : relation.relation_type === 'balancing' ? 'info' : 'ok'}>
                                    {relationTypeLabel}
                                  </StatusBadge>
                                </div>
                              </div>
                              <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-300">
                                <span>Priority: {relation.priority_order ?? '-'}</span>
                                {relation.notes ? <span>Notes: {relation.notes}</span> : <span className="text-slate-500">Notes belum diisi</span>}
                              </div>
                              <div className="mb-2 rounded-md border border-white/10 bg-[#0f1b2e] px-2 py-1.5 text-[11px] text-slate-300">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <span className="text-slate-400">Path:</span>
                                  <StatusBadge tone={relationTelemetryTone(relation.link_status)}>
                                    {String(relation.link_status || 'unknown').toUpperCase()}
                                  </StatusBadge>
                                  <span className="text-slate-400">Usage:</span>
                                  <span className="text-slate-100">{String(relation.path_usage || 'unknown').replaceAll('_', ' ')}</span>
                                </div>
                                <p className="mt-1 text-[10px] text-slate-400">
                                  Traffic: <span className="text-slate-200">{relation.traffic_hint || 'Traffic hint tidak tersedia'}</span>
                                  {' · '}
                                  Last seen: <span className="text-slate-200">{relation.last_seen ? new Date(relation.last_seen).toLocaleString('id-ID') : '-'}</span>
                                </p>
                              </div>
                              <div className="flex flex-wrap items-center gap-1">
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-[11px] text-cyan-100 hover:bg-cyan-500/20"
                                  onClick={() => openEditRelation(relation)}
                                >
                                  <Pencil className="h-3 w-3" />
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-[11px] text-slate-200 hover:bg-white/10"
                                  onClick={() => toggleRelationActive(relation)}
                                >
                                  {relation.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                                </button>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 px-2 py-1 text-[11px] text-rose-200 hover:bg-rose-500/10"
                                  onClick={() => handleDeleteRelation(relation)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                  Hapus
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        {!showAllRelations && routerRelations.length > 3 ? (
                          <button
                            type="button"
                            className="w-full rounded-md border border-white/15 bg-[#0f1a2b] px-2 py-1.5 text-[11px] text-slate-300 hover:bg-[#14243d]"
                            onClick={() => setShowAllRelations(true)}
                          >
                            Tampilkan semua relasi ({routerRelations.length})
                          </button>
                        ) : null}
                        {showAllRelations && routerRelations.length > 3 ? (
                          <button
                            type="button"
                            className="w-full rounded-md border border-white/15 bg-[#0f1a2b] px-2 py-1.5 text-[11px] text-slate-300 hover:bg-[#14243d]"
                            onClick={() => setShowAllRelations(false)}
                          >
                            Tampilkan ringkas (3 relasi)
                          </button>
                        ) : null}
                      </div>
                    ) : null}

                    {relationEditorOpen ? (
                      <form className="mt-3 space-y-2 rounded-md border border-white/10 bg-[#0e1a2d] p-3" onSubmit={submitRelation}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold text-white">{relationEditorMode === 'edit' ? 'Edit Relasi Router' : 'Tambah Relasi Router'}</p>
                          <button
                            type="button"
                            className="rounded-md border border-white/15 px-2 py-1 text-[11px] text-slate-200 hover:bg-white/10"
                            onClick={closeRelationEditor}
                          >
                            Tutup
                          </button>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                          <label className="space-y-1">
                            <span className="text-[11px] text-slate-300">Router Sumber (From)</span>
                            <select
                              value={relationForm.from_router_id}
                              onChange={(event) => setRelationForm((prev) => ({ ...prev, from_router_id: event.target.value }))}
                              className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-2.5 py-2 text-xs text-slate-200"
                            >
                              <option value="">Pilih router sumber</option>
                              {routerOptions.map((router) => (
                                <option key={`from-${router.id}`} value={router.id}>
                                  {router.name} ({router.router_role}) - {router.site}
                                </option>
                              ))}
                            </select>
                            {selectedRouterId ? <p className="text-[10px] text-slate-500">Router sumber diprefill dari router yang sedang dipilih.</p> : null}
                          </label>
                          <label className="space-y-1">
                            <span className="text-[11px] text-slate-300">Router Tujuan (To)</span>
                            <select
                              value={relationForm.to_router_id}
                              onChange={(event) => setRelationForm((prev) => ({ ...prev, to_router_id: event.target.value }))}
                              className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-2.5 py-2 text-xs text-slate-200"
                            >
                              <option value="">Pilih router tujuan</option>
                              {routerOptions.map((router) => (
                                <option key={`to-${router.id}`} value={router.id}>
                                  {router.name} ({router.router_role}) - {router.site}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="space-y-1">
                            <span className="text-[11px] text-slate-300">Relation Type</span>
                            <select
                              value={relationForm.relation_type}
                              onChange={(event) => setRelationForm((prev) => ({ ...prev, relation_type: event.target.value }))}
                              className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-2.5 py-2 text-xs text-slate-200"
                            >
                              {RELATION_TYPE_OPTIONS.map((item) => (
                                <option key={item.value} value={item.value}>{item.label}</option>
                              ))}
                            </select>
                            <p className="text-[10px] text-slate-500">
                              primary = jalur utama, backup = jalur cadangan, balancing = jalur pembagi beban
                            </p>
                          </label>
                          <label className="space-y-1">
                            <span className="text-[11px] text-slate-300">Priority Order</span>
                            <input
                              value={relationForm.priority_order}
                              onChange={(event) => setRelationForm((prev) => ({ ...prev, priority_order: event.target.value }))}
                              placeholder="0"
                              className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-2.5 py-2 text-xs text-slate-100"
                            />
                            <p className="text-[10px] text-slate-500">Semakin kecil nilai, semakin diprioritaskan dalam jalur operasional.</p>
                          </label>
                          <label className="space-y-1 md:col-span-2">
                            <span className="text-[11px] text-slate-300">Notes</span>
                            <textarea
                              value={relationForm.notes}
                              onChange={(event) => setRelationForm((prev) => ({ ...prev, notes: event.target.value }))}
                              rows={2}
                              placeholder="Catatan relasi operasional (opsional)"
                              className="w-full rounded-lg border border-white/10 bg-[#0a1322] px-2.5 py-2 text-xs text-slate-100"
                            />
                            <p className="text-[10px] text-slate-500">Opsional, gunakan untuk konteks teknis singkat (contoh: link metro utama).</p>
                          </label>
                          <label className="inline-flex items-center gap-2 text-xs text-slate-300">
                            <input
                              type="checkbox"
                              checked={Boolean(relationForm.is_active)}
                              onChange={(event) => setRelationForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                            />
                            Relasi aktif
                          </label>
                        </div>
                        {relationFormError ? (
                          <p className="text-xs text-amber-300">{relationFormError}</p>
                        ) : null}
                        <div className="flex justify-end gap-2">
                          <UIButton type="button" variant="ghost" onClick={closeRelationEditor}>
                            Batal
                          </UIButton>
                          <UIButton type="submit" variant="subtle" disabled={relationSubmitting || Boolean(relationFormError)}>
                            {relationSubmitting ? 'Menyimpan...' : relationEditorMode === 'edit' ? 'Simpan Perubahan' : 'Tambah Relasi'}
                          </UIButton>
                        </div>
                      </form>
                    ) : null}
                  </div>
                ) : null}

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Customer Impact</p>
                  {relatedCustomers.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada customer langsung terkait node ini, atau relasi node-customer belum lengkap.</p>
                  ) : (
                    <div className="flex max-h-[120px] flex-wrap gap-1.5 overflow-auto">
                      {relatedCustomers.slice(0, 20).map((customer) => (
                        <span key={customer.id} className="rounded-md border border-white/10 bg-[#13213a] px-2 py-1 text-[11px] text-slate-200">
                          {customer.customer_id || customer.id}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <p className="mb-2 text-xs uppercase tracking-[0.12em] text-slate-400">Related Incidents</p>
                  {relatedIncidents.length === 0 ? (
                    <p className="text-xs text-slate-400">Tidak ada incident aktif pada node ini, atau relasi incident-node belum lengkap.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {relatedIncidents.slice(0, 5).map((incident) => (
                        <div key={incident.id} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-white">{incident.title}</p>
                            <StatusBadge tone={incident.severity === 'critical' ? 'danger' : 'warn'}>
                              {incident.severity.toUpperCase()}
                            </StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-400">{incident.summary}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="rounded-lg border border-white/10 bg-[#0a1322] p-3">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-[0.12em] text-slate-400">Related Tickets</p>
                    <StatusBadge tone="neutral">{activeRelatedTickets.length} ACTIVE</StatusBadge>
                  </div>
                  <p className="mb-2 text-[11px] text-slate-400">
                    Context: {selectedRow.type}:{selectedRow.id}
                    {selectedRow.site ? <> • site {selectedRow.site}</> : null}
                  </p>
                  {relatedTickets.length === 0 ? (
                    <p className="text-xs text-slate-400">Belum ada ticket terkait. Buat ticket untuk mulai melacak penanganan.</p>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-slate-400">
                        <span>Terbaru: <span className="text-slate-100">{latestRelatedTicket?.ticketCode || latestRelatedTicket?.id || '-'}</span></span>
                        <span>Total: <span className="text-slate-100">{relatedTickets.length}</span></span>
                      </div>
                      {(showAllRelatedTickets ? relatedTickets : relatedTickets.slice(0, 3)).map((ticket) => (
                        <div key={ticket.id} className="rounded-md border border-white/10 bg-[#13213a] px-2.5 py-2">
                          <div className="mb-1 flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-white">{ticket.ticketCode || ticket.id}</p>
                            <StatusBadge tone={ticketStatusTone(ticket.status)}>
                              {String(ticket.status || 'open').toLowerCase()}
                            </StatusBadge>
                          </div>
                          <p className="text-[11px] text-slate-300">{ticket.title || '-'}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <StatusBadge tone={ticketPriorityTone(ticket.priority)}>{String(ticket.priority || 'medium').toLowerCase()}</StatusBadge>
                            <span className="text-[10px] text-slate-500">PIC: {ticket.assignedTo || '-'}</span>
                            <span className="text-[10px] text-slate-500">Update: {ticket.updated_at ? new Date(ticket.updated_at).toLocaleString('id-ID') : '-'}</span>
                          </div>
                        </div>
                      ))}
                      {relatedTickets.length > 3 ? (
                        <button
                          type="button"
                          className="text-[11px] text-cyan-300 hover:text-cyan-200"
                          onClick={() => setShowAllRelatedTickets((prev) => !prev)}
                        >
                          {showAllRelatedTickets ? 'Tampilkan ringkas' : 'Tampilkan lebih banyak'}
                        </button>
                      ) : null}
                    </div>
                  )}
                  <div className="mt-2 space-y-2 rounded-md border border-white/10 bg-[#101b2e] p-2.5">
                    <p className="text-[11px] uppercase tracking-[0.1em] text-slate-400">Buat Ticket</p>
                    <input
                      value={ticketDraft.title}
                      onChange={(event) => setTicketDraft((prev) => ({ ...prev, title: event.target.value }))}
                      placeholder={`Issue ${selectedRow.type.toUpperCase()} ${selectedRow.name || selectedRow.id}`}
                      className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <textarea
                      value={ticketDraft.description}
                      onChange={(event) => setTicketDraft((prev) => ({ ...prev, description: event.target.value }))}
                      rows={2}
                      placeholder="Tulis deskripsi issue dan dampak operasional"
                      className="w-full rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={ticketDraft.priority}
                        onChange={(event) => setTicketDraft((prev) => ({ ...prev, priority: event.target.value }))}
                        className="rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-200"
                      >
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                        <option value="critical">critical</option>
                      </select>
                      <input
                        value={ticketDraft.assigned_to}
                        onChange={(event) => setTicketDraft((prev) => ({ ...prev, assigned_to: event.target.value }))}
                        placeholder="PIC / owner"
                        className="rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                      />
                    </div>
                    <UIButton variant="subtle" onClick={handleCreateTicket} disabled={ticketSaving}>
                      Buat Ticket
                    </UIButton>
                    {activeRelatedTickets.length > 0 ? (
                      <p className="text-[11px] text-amber-300">
                        Sudah ada {activeRelatedTickets.length} ticket aktif untuk source ini. Ticket aktif terbaru: {latestRelatedTicket?.ticketCode || latestRelatedTicket?.id || '-'}.
                        Anda tetap bisa membuat ticket baru jika memang perlu.
                      </p>
                    ) : null}
                    {relatedTickets[0] ? (
                      <div className="space-y-2">
                        {(showAllTicketLogs ? (relatedTickets[0].actionLog || []).slice().reverse() : (relatedTickets[0].actionLog || []).slice(-3).reverse()).map((entry) => (
                          <p key={entry.id || `${entry.created_at}-${entry.message}`} className="text-[11px] text-slate-300">
                            {entry.message} <span className="text-slate-500">({entry.actor || '-'} • {entry.created_at ? new Date(entry.created_at).toLocaleString('id-ID') : '-'})</span>
                          </p>
                        ))}
                        {(relatedTickets[0].actionLog || []).length > 3 ? (
                          <button
                            type="button"
                            className="text-[11px] text-cyan-300 hover:text-cyan-200"
                            onClick={() => setShowAllTicketLogs((prev) => !prev)}
                          >
                            {showAllTicketLogs ? 'Tampilkan log terbaru saja' : 'Lihat semua log'}
                          </button>
                        ) : null}
                        <div className="flex gap-2">
                          <input
                            value={ticketUpdateNote}
                            onChange={(event) => setTicketUpdateNote(event.target.value)}
                            placeholder="Tulis update investigasi atau tindakan yang dilakukan"
                            className="flex-1 rounded-md border border-white/10 bg-[#0f1a2b] px-2 py-1.5 text-xs text-slate-100 placeholder:text-slate-500"
                          />
                          <UIButton variant="ghost" onClick={() => handleAddTicketUpdate(relatedTickets[0].id)} disabled={ticketSaving}>
                            Simpan Update
                          </UIButton>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <UIButton as="link" to={topologyLink} variant="subtle">
                    <ArrowUpRight className="h-4 w-4" />
                    Open in Topology
                  </UIButton>
                  <UIButton
                    as="link"
                    to={relatedCustomers[0]
                      ? `/customers?customerId=${encodeURIComponent(String(relatedCustomers[0].id || ''))}&nodeType=${encodeURIComponent(selectedRow.type)}&nodeId=${encodeURIComponent(selectedRow.id)}`
                      : '/customers'}
                    variant="ghost"
                  >
                    View Customer
                  </UIButton>
                  {selectedRow.type === 'olt' ? (
                    <UIButton variant="ghost" onClick={() => {
                      const target = olts.find((item) => String(item.id) === String(selectedRow.id));
                      if (target) openEditOlt(target);
                    }}>
                      Edit OLT
                    </UIButton>
                  ) : null}
                  {selectedRow.type === 'odp' ? (
                    <UIButton variant="ghost" onClick={() => {
                      const target = odps.find((item) => String(item.id) === String(selectedRow.id));
                      if (target) openEditOdp(target);
                    }}>
                      Edit ODP
                    </UIButton>
                  ) : null}
                  {selectedRow.type === 'nas' ? (
                    <UIButton
                      variant="ghost"
                      onClick={() => {
                        if (selectedRouterRecord) {
                          openEditRouter(selectedRouterRecord);
                        } else {
                          toast.error('Router belum tersedia di katalog. Sinkronisasi router diperlukan sebelum edit.');
                        }
                      }}
                    >
                      Edit Router
                    </UIButton>
                  ) : null}
                  {selectedRow.type === 'olt' ? (
                    <UIButton variant="ghost" onClick={() => openCreateOdp({ olt_id: selectedRow.id, site: selectedRow.site })}>
                      Tambah ODP
                    </UIButton>
                  ) : null}
                  {selectedRow.type === 'nas' ? (
                    <UIButton
                      variant="ghost"
                      onClick={() => {
                        if (selectedRouterRecord?.id) {
                          handleDeleteRouter(selectedRouterRecord.id);
                        } else {
                          toast.error('Router belum tersedia di katalog. Hapus dilakukan dari backend/router catalog.');
                        }
                      }}
                    >
                      Hapus Router
                    </UIButton>
                  ) : null}
                  {selectedSiteRecord ? (
                    <UIButton variant="ghost" onClick={() => openEditSite(selectedSiteRecord)}>
                      Edit Site
                    </UIButton>
                  ) : null}
                  {selectedSiteRecord ? (
                    <UIButton variant="ghost" onClick={() => handleDeleteSite(selectedSiteRecord.id)}>
                      Hapus Site
                    </UIButton>
                  ) : null}
                  <UIButton
                    as="link"
                    to={relatedIncidents[0]
                      ? `/recovery?incidentId=${encodeURIComponent(String(relatedIncidents[0].id || ''))}&nodeType=${encodeURIComponent(selectedRow.type)}&nodeId=${encodeURIComponent(selectedRow.id)}`
                      : `/recovery?nodeType=${encodeURIComponent(selectedRow.type)}&nodeId=${encodeURIComponent(selectedRow.id)}`}
                    variant="ghost"
                    className="col-span-2"
                  >
                    Open in Recovery
                  </UIButton>
                </div>
              </>
            )}
          </SectionContainer>
        </div>
      </PageContainer>
    </AppShell>
  );
}
